/* eslint-disable react-hooks/rules-of-hooks, react-hooks/exhaustive-deps */
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { procureiqQueryFromSearch, searchForProcureiq, pageFromPathname, replaceBrowserPath } from "../../routing/paths";
import { pageSignal, combineSignals } from "../../routing/pageAbort";
import { SHIFTIQ_API, getAuthHeaders, isAbortError } from "../../api/client";
import { usePgStores } from "../../stores/StoresProvider";
import { readSelectedStore, writeSelectedStore } from "../../lib/selectedStore";
import { allowedStoresForUser, isSingleStoreLocked } from "../../lib/storeScope";
import { getMonday } from "../../lib/dates";
import { LoadingPulse } from "../../ui";
import { WeekPicker } from "../schedule/ScheduleBuilderPage";

import * as pq from "./helpers";
import {
  getWeekOrdersCache,
  setWeekOrdersCache,
  invalidateWeekOrdersCache,
} from "./weekOrdersCache";
import { buildOrderBuildTrace } from "./orderBuildTrace";
import { SupplierOrderCard } from "./SupplierOrderCard";
import {
  ForecastMenuPanel,
  ForecastLeavesPanel,
  ForecastLeafContribPanel,
  OrderAnalysisPanel,
  OrderBuildPanel,
} from "./ForecastTracePanels";
import { ConsumptionDayPanel } from "./ConsumptionDayPanel";
const WEEKDAY_SLOT_OPTIONS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];
const procureiqApiError = pq.procureiqApiError;
const procureiqLocationKey = pq.procureiqLocationKey;
const procureiqResolveLocationKey = pq.procureiqResolveLocationKey;
const procureiqLocationKeyFromSelection = pq.procureiqLocationKeyFromSelection;
const procureiqInitialOrderQty = pq.procureiqInitialOrderQty;
const procureiqPacksForNeed = pq.procureiqPacksForNeed;
const procureiqDaysOfCover = pq.procureiqDaysOfCover;
const procureiqDisplaySupplierName = pq.procureiqDisplaySupplierName;
const procureiqColorFromName = pq.procureiqColorFromName;
const procureiqSupplierInitial = pq.procureiqSupplierInitial;
const procureiqMinOrderApplies = pq.procureiqMinOrderApplies;
const procureiqEffectiveMinOrderEur = pq.procureiqEffectiveMinOrderEur;
const procureiqEditedQtyForTotal = pq.procureiqEditedQtyForTotal;
const procureiqSupplierLiveTotal = pq.procureiqSupplierLiveTotal;
const procureiqSupplierIsBelowMin = pq.procureiqSupplierIsBelowMin;
const procureiqSupplierHasActionableWork = pq.procureiqSupplierHasActionableWork;
const procureiqLocationWithBrandLabel = pq.procureiqLocationWithBrandLabel;
const procureiqStoreWithBrandLabel = pq.procureiqStoreWithBrandLabel;
const procureiqLocationDotColor = pq.procureiqLocationDotColor;
const procureiqBrandDotColor = pq.procureiqBrandDotColor;
const procureiqFormatOrderHistoryDate = pq.procureiqFormatOrderHistoryDate;
const formatProcureEuro = pq.formatProcureEuro;
const formatProcureEuroCompact = pq.formatProcureEuroCompact;
const procureiqFillPctColor = pq.procureiqFillPctColor;

/** Earliest Monday the WeekPicker allows (ProcureIQ forecasts). */
const PROCUREIQ_WEEK_PICKER_FLOOR = "2025-01-06";

function procureiqLocalIsoDate(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function procureiqCurrentMondayIso() {
  return procureiqLocalIsoDate(getMonday(0));
}

function procureiqForecastTabsNeedWeek(tab) {
  return (
    tab === "today" ||
    tab === "week" ||
    tab === "order-build" ||
    tab === "forecast-menu" ||
    tab === "forecast-leaves" ||
    tab === "forecast-contrib" ||
    tab === "order-analysis"
  );
}

function procureiqParseIsoDate(iso) {
  if (!iso || typeof iso !== "string") return null;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  dt.setHours(0, 0, 0, 0);
  return dt;
}

function procureiqShiftIsoDate(iso, days) {
  const dt = procureiqParseIsoDate(iso);
  if (!dt) return iso;
  dt.setDate(dt.getDate() + days);
  return procureiqLocalIsoDate(dt);
}

function procureiqFormatDayLabel(iso, lang) {
  const dt = procureiqParseIsoDate(iso);
  if (!dt) return iso || "";
  return dt.toLocaleDateString(lang === "gr" ? "el-GR" : "en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

/** Latest complete sales day = local yesterday (today is incomplete). */
function procureiqConsumptionLatestIsoDate() {
  return procureiqShiftIsoDate(procureiqLocalIsoDate(), -1);
}

/** Consumption is sales-backed: never allow today or a future calendar day. */
function procureiqClampConsumptionDate(iso) {
  const latest = procureiqConsumptionLatestIsoDate();
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(String(iso))) return latest;
  return String(iso) > latest ? latest : String(iso);
}

function procureiqInitialQtysFromSuppliers(suppliers) {
  const qtys = {};
  (suppliers || []).forEach((supplier) => {
    (supplier.lines || []).forEach((line) => {
      qtys[line.product_id] = procureiqInitialOrderQty(line.suggested_order_qty);
    });
  });
  return qtys;
}

/** True when this AbortController was replaced by a newer in-flight load. */
function procureiqLoadSuperseded(activeRef, controller) {
  return activeRef.current != null && activeRef.current !== controller;
}

export function ProcureIQPage({ user, lang }) {
  const { storeMap, storeRecords } = usePgStores();
  const isStoreLocked = isSingleStoreLocked(user);
  const allowed = allowedStoresForUser(user);
  const canPickStore =
    ["admin", "brand_manager", "hr"].includes(user.role) ||
    (Array.isArray(allowed) && allowed.length > 1);
  const lockedStoreName = isStoreLocked ? (allowed || [])[0] || null : null;

  const [activeLocations, setActiveLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(canPickStore);
  const [locationsError, setLocationsError] = useState(null);
  const initialProcureQuery = useRef(procureiqQueryFromSearch(
    typeof window !== "undefined" ? window.location.search : ""
  )).current;
  const [selectedLocationKey, setSelectedLocationKey] = useState(null);
  const [activeTab, setActiveTab] = useState(() => initialProcureQuery.tab);
  const [weekStart, setWeekStart] = useState(
    () => initialProcureQuery.weekStart || procureiqCurrentMondayIso()
  );
  const [weekOrders, setWeekOrders] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [orderQtysByDate, setOrderQtysByDate] = useState({});
  const [expandedSuppliers, setExpandedSuppliers] = useState({});
  const [topUpModal, setTopUpModal] = useState(null);
  const [topUpData, setTopUpData] = useState(null);
  const [topUpLoading, setTopUpLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [dropActionBusyKey, setDropActionBusyKey] = useState(null);
  const [sendBusy, setSendBusy] = useState(false);
  const [sendingSupplierId, setSendingSupplierId] = useState(null);
  const [sendNotice, setSendNotice] = useState(null);
  const [toast, setToast] = useState(null);
  const [showStorePicker, setShowStorePicker] = useState(false);
  const [selectedOrderDate, setSelectedOrderDate] = useState(null);
  const [dismissedPackTips, setDismissedPackTips] = useState({});
  const [showSuggestedSuppliers, setShowSuggestedSuppliers] = useState(true);
  const [showNoOrderSuppliers, setShowNoOrderSuppliers] = useState(false);
  const [orderHistory, setOrderHistory] = useState(null);
  const [orderHistoryLoading, setOrderHistoryLoading] = useState(false);
  const [orderHistoryError, setOrderHistoryError] = useState(null);
  const canManageSuppliers = ["admin", "hr", "brand_manager"].includes(user.role);
  const canSeeForecastTrace = canManageSuppliers;
  const [suppliersAdmin, setSuppliersAdmin] = useState(null);
  const [suppliersAdminLoading, setSuppliersAdminLoading] = useState(false);
  const [suppliersAdminError, setSuppliersAdminError] = useState(null);
  const [suppliersSearch, setSuppliersSearch] = useState("");
  const [editingSupplierId, setEditingSupplierId] = useState(null);
  const [supplierEditForm, setSupplierEditForm] = useState(null);
  const [supplierSaveBusy, setSupplierSaveBusy] = useState(false);
  const [supplierSaveError, setSupplierSaveError] = useState(null);
  const [supplierSavedId, setSupplierSavedId] = useState(null);
  const [scheduleCopyFromId, setScheduleCopyFromId] = useState("");
  const [scheduleCopyToIds, setScheduleCopyToIds] = useState([]);
  const [scheduleCopyBusy, setScheduleCopyBusy] = useState(false);
  const supplierSavedTimerRef = useRef(null);
  const [forecastMenu, setForecastMenu] = useState(null);
  const [forecastMenuLoading, setForecastMenuLoading] = useState(false);
  const [forecastMenuError, setForecastMenuError] = useState(null);
  const [forecastLeaves, setForecastLeaves] = useState(null);
  const [forecastLeavesLoading, setForecastLeavesLoading] = useState(false);
  const [forecastLeavesError, setForecastLeavesError] = useState(null);
  const [orderBuild, setOrderBuild] = useState(null);
  const [orderBuildLoading, setOrderBuildLoading] = useState(false);
  const [orderBuildError, setOrderBuildError] = useState(null);
  const [orderBuildMeta, setOrderBuildMeta] = useState(null);
  const [forecastContrib, setForecastContrib] = useState(null);
  const [forecastContribLoading, setForecastContribLoading] = useState(false);
  const [forecastContribError, setForecastContribError] = useState(null);
  const [consumptionDate, setConsumptionDateState] = useState(
    () => procureiqClampConsumptionDate(initialProcureQuery.date)
  );
  const setConsumptionDate = useCallback((iso) => {
    setConsumptionDateState(procureiqClampConsumptionDate(iso));
  }, []);
  const [consumptionView, setConsumptionView] = useState(
    () => initialProcureQuery.view || "materials"
  );
  const [consumptionDay, setConsumptionDay] = useState(null);
  const [consumptionLoading, setConsumptionLoading] = useState(false);
  const [consumptionError, setConsumptionError] = useState(null);
  const [consumptionRefreshBusy, setConsumptionRefreshBusy] = useState(false);
  const [consumptionProgress, setConsumptionProgress] = useState(null);
  const weekOrdersRef = useRef(null);
  const forecastLeavesRef = useRef(null);
  weekOrdersRef.current = weekOrders;
  forecastLeavesRef.current = forecastLeaves;

  const actionRequiredRef = useRef(null);
  const dayStripRef = useRef(null);
  const toastTimerRef = useRef(null);
  const loadAbortRef = useRef(null);
  /** Warm week-orders for Order build without blocking leaves/menu tabs. */
  const weekPrefetchAbortRef = useRef(null);

  const cancelLocationLoad = useCallback(() => {
    loadAbortRef.current?.abort();
    loadAbortRef.current = null;
    weekPrefetchAbortRef.current?.abort();
    weekPrefetchAbortRef.current = null;
    setLoading(false);
    setOrderHistoryLoading(false);
    setSuppliersAdminLoading(false);
    setForecastMenuLoading(false);
    setForecastLeavesLoading(false);
    setForecastContribLoading(false);
    setOrderBuildLoading(false);
    setOrderBuildMeta(null);
    setConsumptionLoading(false);
    setConsumptionRefreshBusy(false);
    setConsumptionProgress(null);
  }, []);

  useEffect(() => {
    if (!canPickStore) {
      setActiveLocations([]);
      setLocationsLoading(false);
      setLocationsError(null);
      setSelectedLocationKey(null);
      return undefined;
    }
    let cancelled = false;
    setLocationsLoading(true);
    setLocationsError(null);
    fetch(`${SHIFTIQ_API}/api/procureiq/active-locations`, { headers: getAuthHeaders(), signal: pageSignal() })
      .then(async (r) => {
        if (!r.ok) throw new Error(await procureiqApiError(r, "Failed to load locations"));
        return r.json();
      })
      .then((data) => {
        if (cancelled) return;
        const locs = Array.isArray(data?.locations) ? data.locations : [];
        setActiveLocations(locs);
        setSelectedLocationKey((prev) => {
          if (prev && locs.some((loc) => procureiqLocationKey(loc) === prev)) return prev;
          const liveQuery = procureiqQueryFromSearch(
            typeof window !== "undefined" ? window.location.search : ""
          );
          const locationId = liveQuery.locationId != null
            ? liveQuery.locationId
            : initialProcureQuery.locationId;
          const brandId = liveQuery.locationId != null
            ? liveQuery.brandId
            : initialProcureQuery.brandId;
          return procureiqLocationKeyFromSelection(locs, {
            locationId,
            brandId,
            remembered: readSelectedStore(),
          });
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setActiveLocations([]);
        setSelectedLocationKey(null);
        setLocationsError(
          err?.message || (lang === "gr" ? "Αποτυχία φόρτωσης τοποθεσιών" : "Failed to load locations")
        );
      })
      .finally(() => {
        if (!cancelled) setLocationsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canPickStore, lang]);

  const selectedLocation = useMemo(() => {
    if (!canPickStore) return null;
    if (!activeLocations.length) return null;
    return (
      activeLocations.find((loc) => procureiqLocationKey(loc) === selectedLocationKey) ||
      activeLocations[0] ||
      null
    );
  }, [canPickStore, activeLocations, selectedLocationKey]);

  const locationId = canPickStore
    ? selectedLocation?.unified_location_id ?? null
    : lockedStoreName
      ? storeMap[lockedStoreName] ?? null
      : null;
  const locationIdRef = useRef(locationId);

  useEffect(() => {
    locationIdRef.current = locationId;
  }, [locationId]);

  useEffect(() => {
    if (canPickStore) return;
    if (!lockedStoreName || locationId != null) return;
    if (typeof console !== "undefined" && console.warn) {
      console.warn(
        "[ProcureIQ] No unified_location_id in storeMap for selected store:",
        lockedStoreName,
        { storeMap }
      );
    }
  }, [canPickStore, lockedStoreName, locationId, storeMap]);

  const applyWeekOrdersPayload = useCallback((data) => {
    const days = Array.isArray(data?.days) ? data.days : [];
    setWeekOrders(data);
    const todayIso = procureiqLocalIsoDate();
    const todayIdx = days.findIndex((d) => d.order_date === todayIso);
    const defaultDay = days[todayIdx >= 0 ? todayIdx : 0] || null;
    setSelectedOrderDate(defaultDay?.order_date || null);
    const qtysByDate = {};
    days.forEach((day) => {
      qtysByDate[day.order_date] = procureiqInitialQtysFromSuppliers(day.suppliers);
    });
    setOrderQtysByDate(qtysByDate);
    setExpandedSuppliers({});
    setShowSuggestedSuppliers(true);
    setShowNoOrderSuppliers(false);
  }, []);

  const loadTodayOrders = useCallback(
    (opts = {}) => {
      const force = !!opts.force;
      if (canPickStore && locationsLoading) return;
      if (!locationId) {
        setLoadError(
          locationsError ||
            (lang === "gr"
              ? "Δεν βρέθηκε location_id για αυτό το κατάστημα"
              : "No location_id found for this store")
        );
        setWeekOrders(null);
        setSelectedOrderDate(null);
        setOrderQtysByDate({});
        setLoading(false);
        return;
      }
      const fetchedForLocationId = locationId;
      const weekStartKey = weekStart || "";

      if (!force) {
        const cached = getWeekOrdersCache(fetchedForLocationId, weekStartKey);
        if (cached) {
          loadAbortRef.current?.abort();
          loadAbortRef.current = null;
          setLoadError(null);
          applyWeekOrdersPayload(cached);
          setLoading(false);
          return;
        }
      } else {
        invalidateWeekOrdersCache(fetchedForLocationId, weekStartKey);
      }

      loadAbortRef.current?.abort();
      const controller = new AbortController();
      loadAbortRef.current = controller;
      setLoading(true);
      setLoadError(null);
      setWeekOrders(null);
      setSelectedOrderDate(null);
      setOrderQtysByDate({});
      setExpandedSuppliers({});
      setShowSuggestedSuppliers(true);
      setShowNoOrderSuppliers(false);
      const weekStartParam = weekStartKey ? `&week_start=${weekStartKey}` : "";
      if (typeof console !== "undefined" && console.log) {
        console.log("[ProcureIQ] loadWeekOrders fetch", {
          locationId: fetchedForLocationId,
          weekStart: weekStartParam || null,
          force,
          selectedLocation: selectedLocation?.location_name || lockedStoreName,
        });
      }
      fetch(
        `${SHIFTIQ_API}/api/procureiq/week-orders?location_id=${fetchedForLocationId}${weekStartParam}`,
        {
          headers: getAuthHeaders(),
          signal: combineSignals(pageSignal(), controller.signal),
        }
      )
        .then(async (r) => {
          if (!r.ok) throw new Error(await procureiqApiError(r, "Failed to load orders"));
          return r.json();
        })
        .then((data) => {
          if (controller.signal.aborted) return;
          if (procureiqLoadSuperseded(loadAbortRef, controller)) return;
          if (fetchedForLocationId !== locationIdRef.current) return;
          setLoadError(null);
          setWeekOrdersCache(fetchedForLocationId, weekStartKey, data);
          applyWeekOrdersPayload(data);
          if (typeof console !== "undefined" && console.log) {
            console.log(
              "[ProcureIQ] week-orders loaded",
              Array.isArray(data?.days) ? data.days.length : 0,
              "days, week_basket_eur",
              data?.week_basket_eur,
              "for locationId",
              fetchedForLocationId
            );
          }
        })
        .catch((err) => {
          if (controller.signal.aborted || isAbortError(err)) {
            if (procureiqLoadSuperseded(loadAbortRef, controller)) return;
            if (fetchedForLocationId !== locationIdRef.current) return;
            setLoadError(
              lang === "gr" ? "Η φόρτωση ακυρώθηκε" : "Load cancelled"
            );
            setWeekOrders(null);
            setSelectedOrderDate(null);
            setOrderQtysByDate({});
            return;
          }
          if (procureiqLoadSuperseded(loadAbortRef, controller)) return;
          if (fetchedForLocationId !== locationIdRef.current) return;
          setLoadError(err?.message || (lang === "gr" ? "Αποτυχία φόρτωσης" : "Failed to load"));
          setWeekOrders(null);
          setSelectedOrderDate(null);
          setOrderQtysByDate({});
        })
        .finally(() => {
          if (procureiqLoadSuperseded(loadAbortRef, controller)) return;
          if (loadAbortRef.current === controller) loadAbortRef.current = null;
          if (fetchedForLocationId !== locationIdRef.current) return;
          setLoading(false);
        });
    },
    [
      locationId,
      lang,
      canPickStore,
      locationsLoading,
      locationsError,
      selectedLocation,
      lockedStoreName,
      applyWeekOrdersPayload,
      weekStart,
    ]
  );

  useEffect(() => {
    // Week-orders is ~4–5s; only fetch when Today/Week need the basket.
    // Forecast tabs (leaves/menu/contrib) and order-build have their own loaders.
    if (activeTab !== "today" && activeTab !== "week") return;
    loadTodayOrders();
  }, [loadTodayOrders, activeTab]);

  const loadOrderHistory = useCallback(() => {
    if (!locationId) {
      setOrderHistoryError(
        lang === "gr"
          ? "Δεν βρέθηκε location_id για αυτό το κατάστημα"
          : "No location_id found for this store"
      );
      setOrderHistory(null);
      setOrderHistoryLoading(false);
      return;
    }
    loadAbortRef.current?.abort();
    const controller = new AbortController();
    loadAbortRef.current = controller;
    setOrderHistoryLoading(true);
    setOrderHistoryError(null);
    setOrderHistory(null);
    fetch(`${SHIFTIQ_API}/api/procureiq/order-history?location_id=${locationId}&limit=20`, {
      headers: getAuthHeaders(),
      signal: combineSignals(pageSignal(), controller.signal),
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(await procureiqApiError(r, "Failed to load order history"));
        return r.json();
      })
      .then((data) => {
        if (controller.signal.aborted) return;
        if (procureiqLoadSuperseded(loadAbortRef, controller)) return;
        setOrderHistoryError(null);
        setOrderHistory(data);
      })
      .catch((err) => {
        if (controller.signal.aborted || isAbortError(err)) {
          if (procureiqLoadSuperseded(loadAbortRef, controller)) return;
          setOrderHistoryError(
            lang === "gr" ? "Η φόρτωση ακυρώθηκε" : "Load cancelled"
          );
          setOrderHistory(null);
          return;
        }
        if (procureiqLoadSuperseded(loadAbortRef, controller)) return;
        setOrderHistoryError(err?.message || (lang === "gr" ? "Αποτυχία φόρτωσης" : "Failed to load"));
        setOrderHistory(null);
      })
      .finally(() => {
        if (procureiqLoadSuperseded(loadAbortRef, controller)) return;
        if (loadAbortRef.current === controller) loadAbortRef.current = null;
        setOrderHistoryLoading(false);
      });
  }, [locationId, lang]);

  useEffect(() => {
    if (activeTab !== "history") return;
    loadOrderHistory();
  }, [activeTab, loadOrderHistory]);

  const loadSuppliersAdmin = useCallback(() => {
    if (!canManageSuppliers) return;
    if (!locationId) {
      setSuppliersAdminError(
        lang === "gr"
          ? "Δεν βρέθηκε location_id για αυτό το κατάστημα"
          : "No location_id found for this store"
      );
      setSuppliersAdmin(null);
      setSuppliersAdminLoading(false);
      return;
    }
    const fetchedForLocationId = locationId;
    loadAbortRef.current?.abort();
    const controller = new AbortController();
    loadAbortRef.current = controller;
    setSuppliersAdminLoading(true);
    setSuppliersAdminError(null);
    setSuppliersAdmin(null);
    fetch(`${SHIFTIQ_API}/api/procureiq/suppliers?location_id=${fetchedForLocationId}`, {
      headers: getAuthHeaders(),
      signal: combineSignals(pageSignal(), controller.signal),
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(await procureiqApiError(r, "Failed to load suppliers"));
        return r.json();
      })
      .then((data) => {
        if (controller.signal.aborted) return;
        if (procureiqLoadSuperseded(loadAbortRef, controller)) return;
        if (fetchedForLocationId !== locationIdRef.current) return;
        setSuppliersAdminError(null);
        setSuppliersAdmin(data);
      })
      .catch((err) => {
        if (controller.signal.aborted || isAbortError(err)) {
          if (procureiqLoadSuperseded(loadAbortRef, controller)) return;
          if (fetchedForLocationId !== locationIdRef.current) return;
          setSuppliersAdminError(
            lang === "gr" ? "Η φόρτωση ακυρώθηκε" : "Load cancelled"
          );
          setSuppliersAdmin(null);
          return;
        }
        if (procureiqLoadSuperseded(loadAbortRef, controller)) return;
        if (fetchedForLocationId !== locationIdRef.current) return;
        setSuppliersAdminError(err?.message || (lang === "gr" ? "Αποτυχία φόρτωσης" : "Failed to load"));
        setSuppliersAdmin(null);
      })
      .finally(() => {
        if (procureiqLoadSuperseded(loadAbortRef, controller)) return;
        if (loadAbortRef.current === controller) loadAbortRef.current = null;
        if (fetchedForLocationId !== locationIdRef.current) return;
        setSuppliersAdminLoading(false);
      });
  }, [canManageSuppliers, locationId, lang]);

  useEffect(() => {
    if (!canManageSuppliers || activeTab !== "suppliers") return;
    setEditingSupplierId(null);
    setSupplierEditForm(null);
    setSupplierSaveError(null);
    loadSuppliersAdmin();
  }, [canManageSuppliers, activeTab, loadSuppliersAdmin]);

  useEffect(() => {
    if (pageFromPathname(window.location.pathname) !== "procureiq") return undefined;
    if (canPickStore && locationsLoading) return undefined;
    const id = canPickStore ? selectedLocation?.unified_location_id : locationId;
    const brandId = canPickStore ? selectedLocation?.brand_id : undefined;
    replaceBrowserPath(
      "/procureiq",
      searchForProcureiq({
        tab: activeTab,
        locationId: id,
        brandId,
        weekStart: procureiqForecastTabsNeedWeek(activeTab) ? weekStart : undefined,
        date: activeTab === "consumption" ? consumptionDate : undefined,
        view: activeTab === "consumption" ? consumptionView : undefined,
      })
    );
    return undefined;
  }, [
    activeTab,
    canPickStore,
    locationsLoading,
    selectedLocation,
    locationId,
    weekStart,
    consumptionDate,
    consumptionView,
  ]);

  useEffect(() => {
    function onPop() {
      if (pageFromPathname(window.location.pathname) !== "procureiq") return;
      const query = procureiqQueryFromSearch(window.location.search);
      setActiveTab(query.tab);
      if (query.weekStart) setWeekStart(query.weekStart);
      if (query.date) setConsumptionDate(query.date);
      if (query.view) setConsumptionView(query.view);
      setSelectedLocationKey((prev) => {
        if (query.locationId == null) return prev;
        return (
          procureiqResolveLocationKey(activeLocations, query.locationId, query.brandId) ||
          prev
        );
      });
    }
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [activeLocations]);

  useEffect(() => {
    if (canManageSuppliers) return;
    if (
      activeTab === "suppliers" ||
      activeTab === "forecast-menu" ||
      activeTab === "forecast-leaves" ||
      activeTab === "forecast-contrib" ||
      activeTab === "order-build" ||
      activeTab === "order-analysis" ||
      activeTab === "receipt-review"
    ) {
      setActiveTab("consumption");
    }
    setEditingSupplierId(null);
    setSupplierEditForm(null);
  }, [canManageSuppliers, activeTab]);

  const weekStartQueryParam = useMemo(() => {
    if (!weekStart) return "";
    return `&week_start=${weekStart}`;
  }, [weekStart]);

  const commitProcureWeek = useCallback((mondayISO) => {
    if (!mondayISO || mondayISO === weekStart) return;
    setWeekStart(mondayISO);
    setForecastMenu(null);
    setForecastLeaves(null);
    setForecastContrib(null);
    setOrderBuild(null);
    setOrderBuildMeta(null);
  }, [weekStart]);

  const loadForecastMenu = useCallback(() => {
    if (!canSeeForecastTrace || !locationId) return;
    loadAbortRef.current?.abort();
    const controller = new AbortController();
    loadAbortRef.current = controller;
    setForecastMenuLoading(true);
    setForecastMenuError(null);
    setForecastMenu(null);
    fetch(
      `${SHIFTIQ_API}/api/procureiq/forecast-trace/menu?location_id=${locationId}${weekStartQueryParam}`,
      { headers: getAuthHeaders(), signal: combineSignals(pageSignal(), controller.signal) }
    )
      .then(async (r) => {
        if (!r.ok) throw new Error(await procureiqApiError(r, "Failed to load forecast menu"));
        return r.json();
      })
      .then((data) => {
        if (controller.signal.aborted) return;
        if (procureiqLoadSuperseded(loadAbortRef, controller)) return;
        setForecastMenuError(null);
        setForecastMenu(data);
      })
      .catch((err) => {
        if (controller.signal.aborted || isAbortError(err)) {
          if (procureiqLoadSuperseded(loadAbortRef, controller)) return;
          setForecastMenuError(lang === "gr" ? "Η φόρτωση ακυρώθηκε" : "Load cancelled");
          return;
        }
        if (procureiqLoadSuperseded(loadAbortRef, controller)) return;
        setForecastMenuError(err?.message || (lang === "gr" ? "Αποτυχία φόρτωσης" : "Failed to load"));
      })
      .finally(() => {
        if (procureiqLoadSuperseded(loadAbortRef, controller)) return;
        if (loadAbortRef.current === controller) loadAbortRef.current = null;
        setForecastMenuLoading(false);
      });
  }, [canSeeForecastTrace, locationId, weekStartQueryParam, lang]);

  const loadForecastLeaves = useCallback(() => {
    if (!canSeeForecastTrace || !locationId) return;
    loadAbortRef.current?.abort();
    const controller = new AbortController();
    loadAbortRef.current = controller;
    setForecastLeavesLoading(true);
    setForecastLeavesError(null);
    setForecastLeaves(null);
    fetch(
      `${SHIFTIQ_API}/api/procureiq/forecast-trace/leaves?location_id=${locationId}${weekStartQueryParam}`,
      { headers: getAuthHeaders(), signal: combineSignals(pageSignal(), controller.signal) }
    )
      .then(async (r) => {
        if (!r.ok) throw new Error(await procureiqApiError(r, "Failed to load forecast leaves"));
        return r.json();
      })
      .then((data) => {
        if (controller.signal.aborted) return;
        if (procureiqLoadSuperseded(loadAbortRef, controller)) return;
        setForecastLeavesError(null);
        setForecastLeaves(data);
      })
      .catch((err) => {
        if (controller.signal.aborted || isAbortError(err)) {
          if (procureiqLoadSuperseded(loadAbortRef, controller)) return;
          setForecastLeavesError(lang === "gr" ? "Η φόρτωση ακυρώθηκε" : "Load cancelled");
          return;
        }
        if (procureiqLoadSuperseded(loadAbortRef, controller)) return;
        setForecastLeavesError(err?.message || (lang === "gr" ? "Αποτυχία φόρτωσης" : "Failed to load"));
      })
      .finally(() => {
        if (procureiqLoadSuperseded(loadAbortRef, controller)) return;
        if (loadAbortRef.current === controller) loadAbortRef.current = null;
        setForecastLeavesLoading(false);
      });
  }, [canSeeForecastTrace, locationId, weekStartQueryParam, lang]);

  const prefetchWeekOrdersQuiet = useCallback(() => {
    if (!canSeeForecastTrace || !locationId) return;
    const weekStartKey = weekStart || "";
    const live = weekOrdersRef.current;
    if (
      live?.days &&
      Number(live.location_id) === Number(locationId) &&
      (!weekStartKey || live.week_start === weekStartKey)
    ) {
      return;
    }
    if (getWeekOrdersCache(locationId, weekStartKey)?.days) return;

    weekPrefetchAbortRef.current?.abort();
    const controller = new AbortController();
    weekPrefetchAbortRef.current = controller;
    fetch(
      `${SHIFTIQ_API}/api/procureiq/week-orders?location_id=${locationId}${weekStartQueryParam}`,
      {
        headers: getAuthHeaders(),
        signal: combineSignals(pageSignal(), controller.signal),
      }
    )
      .then(async (r) => {
        if (!r.ok) throw new Error(await procureiqApiError(r, "Failed to load week orders"));
        return r.json();
      })
      .then((data) => {
        if (controller.signal.aborted) return;
        if (weekPrefetchAbortRef.current !== controller) return;
        setWeekOrdersCache(locationId, weekStartKey, data);
        // Keep Today/Week state in sync if still on the same store/week.
        if (Number(locationIdRef.current) === Number(locationId)) {
          applyWeekOrdersPayload(data);
        }
      })
      .catch((err) => {
        if (controller.signal.aborted || isAbortError(err)) return;
        /* quiet prefetch — ignore */
      })
      .finally(() => {
        if (weekPrefetchAbortRef.current === controller) {
          weekPrefetchAbortRef.current = null;
        }
      });
  }, [
    canSeeForecastTrace,
    locationId,
    weekStart,
    weekStartQueryParam,
    applyWeekOrdersPayload,
  ]);

  const loadOrderBuild = useCallback(
    (opts = {}) => {
      if (!canSeeForecastTrace || !locationId) return;
      const force = !!opts.force;
      const quiet = !!opts.quiet;
      const weekStartKey = weekStart || "";

      loadAbortRef.current?.abort();
      const controller = new AbortController();
      loadAbortRef.current = controller;
      const signal = combineSignals(pageSignal(), controller.signal);

      if (!quiet) {
        setOrderBuildLoading(true);
        setOrderBuildError(null);
        // Keep prior panel visible while refreshing so tab doesn't feel blank.
        if (force) {
          setOrderBuild(null);
          setOrderBuildMeta(null);
        }
      }

      const leavesReusable = (leaves) => {
        if (!leaves?.lines) return false;
        if (Number(leaves.location_id) !== Number(locationId)) return false;
        if (weekStartKey && leaves.week_start && leaves.week_start !== weekStartKey) {
          return false;
        }
        return true;
      };

      const resolveWeekPayload = async () => {
        if (!force) {
          const live = weekOrdersRef.current;
          if (
            live?.days &&
            Number(live.location_id) === Number(locationId) &&
            (!weekStartKey || live.week_start === weekStartKey)
          ) {
            return { week: live, weekSource: "memory" };
          }
          const cached = getWeekOrdersCache(locationId, weekStartKey);
          if (cached?.days) {
            applyWeekOrdersPayload(cached);
            return { week: cached, weekSource: "cache" };
          }
        } else {
          invalidateWeekOrdersCache(locationId, weekStartKey);
        }
        const r = await fetch(
          `${SHIFTIQ_API}/api/procureiq/week-orders?location_id=${locationId}${weekStartQueryParam}`,
          { headers: getAuthHeaders(), signal }
        );
        if (!r.ok) throw new Error(await procureiqApiError(r, "Failed to load week orders"));
        const data = await r.json();
        if (controller.signal.aborted) return null;
        setWeekOrdersCache(locationId, weekStartKey, data);
        applyWeekOrdersPayload(data);
        return { week: data, weekSource: "network" };
      };

      const resolveLeavesPayload = async () => {
        if (!force) {
          const live = forecastLeavesRef.current;
          if (leavesReusable(live)) {
            return { leaves: live, leavesSource: "memory" };
          }
        }
        const r = await fetch(
          `${SHIFTIQ_API}/api/procureiq/forecast-trace/leaves?location_id=${locationId}${weekStartQueryParam}`,
          { headers: getAuthHeaders(), signal }
        );
        if (!r.ok) throw new Error(await procureiqApiError(r, "Failed to load forecast leaves"));
        const data = await r.json();
        if (controller.signal.aborted) return null;
        setForecastLeaves(data);
        return { leaves: data, leavesSource: "network" };
      };

      (async () => {
        try {
          // Week-orders (~4–5s) and leaves (~1s) in parallel — not sequential.
          const [weekResolved, leavesResolved] = await Promise.all([
            resolveWeekPayload(),
            resolveLeavesPayload(),
          ]);
          if (!weekResolved || !leavesResolved || controller.signal.aborted) return;
          if (procureiqLoadSuperseded(loadAbortRef, controller)) return;
          const data = buildOrderBuildTrace(leavesResolved.leaves, weekResolved.week);
          setOrderBuildError(null);
          setOrderBuild(data);
          setOrderBuildMeta({
            weekSource: weekResolved.weekSource,
            leavesSource: leavesResolved.leavesSource,
            reusedWeek: weekResolved.weekSource !== "network",
          });
        } catch (err) {
          if (controller.signal.aborted || isAbortError(err)) {
            if (procureiqLoadSuperseded(loadAbortRef, controller)) return;
            setOrderBuildError(lang === "gr" ? "Η φόρτωση ακυρώθηκε" : "Load cancelled");
            return;
          }
          if (procureiqLoadSuperseded(loadAbortRef, controller)) return;
          setOrderBuildError(
            err?.message || (lang === "gr" ? "Αποτυχία φόρτωσης" : "Failed to load")
          );
        } finally {
          if (procureiqLoadSuperseded(loadAbortRef, controller)) return;
          if (loadAbortRef.current === controller) loadAbortRef.current = null;
          if (!quiet) setOrderBuildLoading(false);
        }
      })();
    },
    [
      canSeeForecastTrace,
      locationId,
      weekStart,
      weekStartQueryParam,
      lang,
      applyWeekOrdersPayload,
    ]
  );

  const loadForecastContrib = useCallback(() => {
    if (!canSeeForecastTrace || !locationId) return;
    loadAbortRef.current?.abort();
    const controller = new AbortController();
    loadAbortRef.current = controller;
    setForecastContribLoading(true);
    setForecastContribError(null);
    setForecastContrib(null);
    fetch(
      `${SHIFTIQ_API}/api/procureiq/forecast-trace/leaf-contrib?location_id=${locationId}${weekStartQueryParam}`,
      { headers: getAuthHeaders(), signal: combineSignals(pageSignal(), controller.signal) }
    )
      .then(async (r) => {
        if (!r.ok) throw new Error(await procureiqApiError(r, "Failed to load leaf sources"));
        return r.json();
      })
      .then((data) => {
        if (controller.signal.aborted) return;
        if (procureiqLoadSuperseded(loadAbortRef, controller)) return;
        setForecastContribError(null);
        setForecastContrib(data);
      })
      .catch((err) => {
        if (controller.signal.aborted || isAbortError(err)) {
          if (procureiqLoadSuperseded(loadAbortRef, controller)) return;
          setForecastContribError(lang === "gr" ? "Η φόρτωση ακυρώθηκε" : "Load cancelled");
          return;
        }
        if (procureiqLoadSuperseded(loadAbortRef, controller)) return;
        setForecastContribError(err?.message || (lang === "gr" ? "Αποτυχία φόρτωσης" : "Failed to load"));
      })
      .finally(() => {
        if (procureiqLoadSuperseded(loadAbortRef, controller)) return;
        if (loadAbortRef.current === controller) loadAbortRef.current = null;
        setForecastContribLoading(false);
      });
  }, [canSeeForecastTrace, locationId, weekStartQueryParam, lang]);

  const loadConsumptionDay = useCallback(
    ({ forceRefresh = false } = {}) => {
      if (!locationId || !consumptionDate) return;
      loadAbortRef.current?.abort();
      const controller = new AbortController();
      loadAbortRef.current = controller;
      if (forceRefresh) {
        setConsumptionRefreshBusy(true);
      } else {
        setConsumptionLoading(true);
      }
      setConsumptionError(null);
      if (!forceRefresh) setConsumptionDay(null);

      // Cold compute runs in a background job on the API (proxy ~100s caps).
      // Short GETs + poll until available — do not hold one long HTTP request.
      const startedAt = Date.now();
      const POLL_MS = 1500;
      const MAX_WAIT_MS = 10 * 60 * 1000;
      const signal = combineSignals(pageSignal(), controller.signal);
      const progressRef = { current: null };
      let elapsedTimer = null;

      const applyProgress = (prog, fallbackMsg) => {
        if (controller.signal.aborted) return;
        if (procureiqLoadSuperseded(loadAbortRef, controller)) return;
        const elapsed = Math.max(0, (Date.now() - startedAt) / 1000);
        const next = {
          phase: prog?.phase || progressRef.current?.phase || "computing",
          message:
            prog?.message ||
            fallbackMsg ||
            progressRef.current?.message ||
            (lang === "gr" ? "Υπολογισμός κατανάλωσης…" : "Building consumption…"),
          detail:
            prog?.detail !== undefined
              ? prog.detail || null
              : progressRef.current?.detail || null,
          pct: Number(prog?.pct) || progressRef.current?.pct || 8,
          elapsed_sec: Math.round(elapsed),
          busy: true,
        };
        progressRef.current = next;
        setConsumptionProgress(next);
      };

      // Tick every second so the counter does not jump only when polls return.
      elapsedTimer = setInterval(() => {
        if (controller.signal.aborted) return;
        applyProgress(progressRef.current);
      }, 1000);

      const sleep = (ms) =>
        new Promise((resolve, reject) => {
          const t = setTimeout(resolve, ms);
          const onAbort = () => {
            clearTimeout(t);
            reject(new DOMException("Aborted", "AbortError"));
          };
          if (signal.aborted) {
            onAbort();
            return;
          }
          signal.addEventListener("abort", onAbort, { once: true });
        });

      const fetchDayOnce = async (useRefresh) => {
        const r = useRefresh
          ? await fetch(`${SHIFTIQ_API}/api/procureiq/consumption/day/refresh`, {
              method: "POST",
              headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
              body: JSON.stringify({
                unified_location_id: Number(locationId),
                date: consumptionDate,
              }),
              signal,
            })
          : await fetch(
              `${SHIFTIQ_API}/api/procureiq/consumption/day?location_id=${locationId}&date=${encodeURIComponent(consumptionDate)}`,
              { headers: getAuthHeaders(), signal }
            );
        if (!r.ok) throw new Error(await procureiqApiError(r, "Failed to load consumption"));
        return r.json();
      };

      const fetchProgress = async () => {
        try {
          const r = await fetch(
            `${SHIFTIQ_API}/api/procureiq/consumption/day/progress?location_id=${locationId}&date=${encodeURIComponent(consumptionDate)}`,
            { headers: getAuthHeaders(), signal }
          );
          if (!r.ok) return null;
          return r.json();
        } catch {
          return null;
        }
      };

      (async () => {
        try {
          applyProgress(null, lang === "gr" ? "Έναρξη…" : "Starting…");
          let data = await fetchDayOnce(forceRefresh);
          while (data?.computing && !controller.signal.aborted) {
            if (procureiqLoadSuperseded(loadAbortRef, controller)) return;
            if (Date.now() - startedAt > MAX_WAIT_MS) {
              throw new Error(
                lang === "gr"
                  ? "Ο υπολογισμός άργησε πολύ — δοκιμάστε ξανά"
                  : "Compute took too long — try again"
              );
            }
            applyProgress(data.progress);
            const prog = await fetchProgress();
            if (prog) applyProgress(prog);
            await sleep(POLL_MS);
            // Re-fetch day (never force on poll — that would loop forever).
            data = await fetchDayOnce(false);
          }
          if (controller.signal.aborted) return;
          if (procureiqLoadSuperseded(loadAbortRef, controller)) return;
          if (data?.available === false && data?.reason && data.reason !== "computing") {
            throw new Error(String(data.reason));
          }
          setConsumptionError(null);
          setConsumptionDay(data);
        } catch (err) {
          if (controller.signal.aborted || isAbortError(err)) {
            if (procureiqLoadSuperseded(loadAbortRef, controller)) return;
            setConsumptionError(lang === "gr" ? "Η φόρτωση ακυρώθηκε" : "Load cancelled");
            return;
          }
          if (procureiqLoadSuperseded(loadAbortRef, controller)) return;
          setConsumptionError(
            err?.message || (lang === "gr" ? "Αποτυχία φόρτωσης" : "Failed to load")
          );
        } finally {
          if (elapsedTimer) clearInterval(elapsedTimer);
          if (procureiqLoadSuperseded(loadAbortRef, controller)) return;
          if (loadAbortRef.current === controller) loadAbortRef.current = null;
          setConsumptionLoading(false);
          setConsumptionRefreshBusy(false);
          setConsumptionProgress(null);
        }
      })();
    },
    [locationId, consumptionDate, lang]
  );

  useEffect(() => {
    if (activeTab !== "consumption") return;
    if (!locationId) return;
    loadConsumptionDay();
  }, [activeTab, locationId, consumptionDate, loadConsumptionDay]);

  useEffect(() => {
    if (!canSeeForecastTrace || activeTab !== "forecast-menu") return;
    loadForecastMenu();
  }, [canSeeForecastTrace, activeTab, loadForecastMenu]);

  useEffect(() => {
    if (!canSeeForecastTrace || activeTab !== "forecast-leaves") return;
    loadForecastLeaves();
  }, [canSeeForecastTrace, activeTab, loadForecastLeaves]);

  useEffect(() => {
    if (!canSeeForecastTrace || activeTab !== "forecast-contrib") return;
    loadForecastContrib();
  }, [canSeeForecastTrace, activeTab, loadForecastContrib]);

  useEffect(() => {
    if (!canSeeForecastTrace || activeTab !== "order-build") return;
    loadOrderBuild();
  }, [canSeeForecastTrace, activeTab, loadOrderBuild]);

  useEffect(() => {
    if (!canSeeForecastTrace || activeTab !== "order-analysis") return;
    // Analysis reuses week-orders + leaves (same loaders as order-build).
    loadOrderBuild({ quiet: true });
    if (!forecastLeavesRef.current?.lines) loadForecastLeaves();
  }, [canSeeForecastTrace, activeTab, loadOrderBuild, loadForecastLeaves]);

  // Warm week-orders while browsing other forecast tabs so Order build is cached.
  useEffect(() => {
    if (!canSeeForecastTrace || !locationId) return;
    if (
      activeTab !== "forecast-leaves" &&
      activeTab !== "forecast-menu" &&
      activeTab !== "forecast-contrib"
    ) {
      return;
    }
    prefetchWeekOrdersQuiet();
  }, [canSeeForecastTrace, activeTab, locationId, prefetchWeekOrdersQuiet]);

  useEffect(() => {
    return () => {
      if (supplierSavedTimerRef.current) clearTimeout(supplierSavedTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!topUpModal || !locationId) return undefined;
    let cancelled = false;
    setTopUpLoading(true);
    setTopUpData(null);
    fetch(
      `${SHIFTIQ_API}/api/procureiq/top-up-candidates?location_id=${locationId}&supplier_id=${topUpModal.supplierId}`,
      { headers: getAuthHeaders(), signal: pageSignal() }
    )
      .then(async (r) => {
        if (!r.ok) throw new Error(await procureiqApiError(r, "Failed to load top-up options"));
        return r.json();
      })
      .then((data) => {
        if (!cancelled) setTopUpData(data);
      })
      .catch((err) => {
        if (!cancelled) {
          showProcureToast(err?.message || (lang === "gr" ? "Σφάλμα" : "Error"));
          setTopUpModal(null);
        }
      })
      .finally(() => {
        if (!cancelled) setTopUpLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- showProcureToast is stable enough
  }, [topUpModal, locationId, lang]);

  function showProcureToast(message) {
    setToast(message);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 2800);
  }

  const weekDays = weekOrders?.days || [];
  const selectedDayIdx = useMemo(() => {
    if (!selectedOrderDate || !weekDays.length) return 0;
    const idx = weekDays.findIndex((d) => d.order_date === selectedOrderDate);
    return idx >= 0 ? idx : 0;
  }, [selectedOrderDate, weekDays]);
  const activeDay = weekDays[selectedDayIdx] || null;
  const todayOrders = useMemo(() => {
    if (!weekOrders || !activeDay) return null;
    return {
      location_id: weekOrders.location_id,
      location_name: weekOrders.location_name,
      order_date: activeDay.order_date,
      week_start: weekOrders.week_start,
      suppliers: activeDay.suppliers || [],
      supplier_count: activeDay.supplier_count ?? (activeDay.suppliers || []).length,
      line_count: activeDay.line_count ?? 0,
      method_used_counts: weekOrders.method_used_counts,
      drops: weekOrders.drops || activeDay.drops,
      is_leaf_forecast_week: !!weekOrders.is_leaf_forecast_week,
    };
  }, [weekOrders, activeDay]);
  const orderQtys = selectedOrderDate
    ? orderQtysByDate[selectedOrderDate] || {}
    : {};
  const setOrderQtys = useCallback(
    (updater) => {
      if (!selectedOrderDate) return;
      setOrderQtysByDate((prev) => {
        const current = prev[selectedOrderDate] || {};
        const next =
          typeof updater === "function" ? updater(current) : updater;
        return { ...prev, [selectedOrderDate]: next };
      });
    },
    [selectedOrderDate]
  );

  const dayPills = useMemo(() => {
    const todayIso = procureiqLocalIsoDate();
    return weekDays.map((day, idx) => ({
      idx,
      orderDate: day.order_date,
      date: procureiqParseIsoDate(day.order_date) || new Date(),
      isToday: day.order_date === todayIso || !!day.is_today,
      basketEur: Number(day.basket_eur) || 0,
      supplierCount: Number(day.supplier_count) || 0,
      withQty: (day.suppliers || []).filter((s) => {
        const qtys = orderQtysByDate[day.order_date] || {};
        return procureiqSupplierLiveTotal(s, qtys) > 0;
      }).length,
      liveTotal: (() => {
        const qtys = orderQtysByDate[day.order_date] || {};
        let sum = 0;
        (day.suppliers || []).forEach((s) => {
          sum += procureiqSupplierLiveTotal(s, qtys);
        });
        return Math.round(sum * 100) / 100;
      })(),
    }));
  }, [weekDays, orderQtysByDate]);

  const suppliers = todayOrders?.suppliers || [];
  const isLeafRecommendOnly = !!weekOrders?.is_leaf_forecast_week;
  const canSendSelectedDay =
    !!activeDay &&
    (activeDay.order_date === procureiqLocalIsoDate() || !!activeDay.is_today) &&
    !isLeafRecommendOnly;

  const sendTodayTotal = useMemo(() => {
    let sum = 0;
    suppliers.forEach((s) => {
      sum += procureiqSupplierLiveTotal(s, orderQtys);
    });
    return Math.round(sum * 100) / 100;
  }, [suppliers, orderQtys]);

  const supplierCount = todayOrders?.supplier_count ?? suppliers.length;

  const blockedSuppliers = useMemo(
    () => suppliers.filter((s) => procureiqSupplierIsBelowMin(s, orderQtys)),
    [suppliers, orderQtys]
  );
  const blockedCount = blockedSuppliers.length;
  const sendBlocked = blockedCount > 0;

  const supplierGroups = useMemo(() => {
    const cover = (supplier) => {
      const lines = supplier.lines || [];
      if (!lines.length) return Number.POSITIVE_INFINITY;
      return Math.min(...lines.map((line) => procureiqDaysOfCover(line)));
    };
    const bySoonest = (a, b) => {
      const diff = cover(a) - cover(b);
      if (diff !== 0) return diff;
      const need = (supplier) => (supplier.lines || []).reduce(
        (sum, line) => sum + (Number(line.predicted_window_qty) || 0),
        0
      );
      return need(b) - need(a);
    };
    const inOrder = [];
    const suggested = [];
    const noOrder = [];
    suppliers.forEach((supplier) => {
      const hasQty = (supplier.lines || []).some(
        (line) => procureiqEditedQtyForTotal(orderQtys, line.product_id) > 0
      );
      if (hasQty) inOrder.push(supplier);
      else if (procureiqSupplierHasActionableWork(supplier, orderQtys)) suggested.push(supplier);
      else noOrder.push(supplier);
    });
    suggested.sort(bySoonest);
    noOrder.sort(bySoonest);
    return { inOrder, suggested, noOrder };
  }, [suppliers, orderQtys]);

  const worthLookItems = useMemo(() => {
    const items = [];
    suppliers.forEach((s) => {
      (s.lines || []).forEach((line) => {
        if (line.needs_stock_count && (Number(line.predicted_daily_qty) || 0) > 0) {
          items.push({
            product_name: line.product_name,
            supplier_name: procureiqDisplaySupplierName(s.supplier_name),
            case_unit: line.case_unit,
            needs_stock_count: true,
          });
        }
      });
    });
    return items;
  }, [suppliers]);

  const topUpSupplierMeta = useMemo(() => {
    if (!topUpModal) return null;
    return suppliers.find((s) => s.supplier_id === topUpModal.supplierId) || null;
  }, [topUpModal, suppliers]);

  function toggleSupplierExpanded(supplierId) {
    setExpandedSuppliers((prev) => ({ ...prev, [supplierId]: !prev[supplierId] }));
  }

  function selectLinePackage(productId, packagingId, packsOverride) {
    const line = suppliers.flatMap((supplier) => supplier.lines || []).find(
      (row) => row.product_id === productId
    );
    const pkg = (line?.packages || []).find((row) => row.packaging_id === packagingId);
    if (!line || !pkg) return;
    const packs =
      packsOverride != null && Number.isFinite(Number(packsOverride))
        ? Math.max(0, Number(packsOverride))
        : procureiqPacksForNeed(line.base_need_qty, pkg.units_per_pack);
    setOrderQtys((prev) => ({ ...prev, [productId]: packs }));
    setWeekOrders((prev) => {
      if (!prev || !selectedOrderDate) return prev;
      return {
        ...prev,
        days: (prev.days || []).map((day) => {
          if (day.order_date !== selectedOrderDate) return day;
          return {
            ...day,
            suppliers: (day.suppliers || []).map((supplier) => ({
              ...supplier,
              lines: (supplier.lines || []).map((row) => {
                if (row.product_id !== productId) return row;
                return {
                  ...row,
                  selected_package_id: pkg.packaging_id,
                  unit_cost: pkg.unit_price,
                  order_unit:
                    pkg.unit_name && !/^[×(]/.test(pkg.unit_name) ? pkg.unit_name : "",
                  units_per_pack: pkg.units_per_pack,
                  suggested_order_qty: packs,
                  line_total: Math.round(packs * Number(pkg.unit_price) * 100) / 100,
                };
              }),
            })),
          };
        }),
      };
    });
  }

  async function postPackChoiceEvent(payload) {
    try {
      await fetch(`${SHIFTIQ_API}/api/procureiq/pack-choice-event`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
    } catch {
      /* learning is best-effort */
    }
  }

  function acceptPackSuggestion(line, tip) {
    if (!line || !tip?.packaging_id) return;
    const tipPacks =
      tip.packs != null && Number.isFinite(Number(tip.packs))
        ? Number(tip.packs)
        : undefined;
    selectLinePackage(line.product_id, tip.packaging_id, tipPacks);
    setDismissedPackTips((prev) => ({ ...prev, [String(line.product_id)]: true }));
    if (line.supy_item_id && locationId != null) {
      postPackChoiceEvent({
        unified_location_id: locationId,
        item_id: line.supy_item_id,
        order_date: selectedOrderDate || undefined,
        event: "accept",
        suggested_packaging_id: tip.packaging_id,
        chosen_packaging_id: tip.packaging_id,
        horizon_days: tip.horizon_days ?? null,
        base_need: tip.horizon_need_base ?? line.base_need_qty ?? null,
      });
    }
  }

  function dismissPackSuggestion(line, tip) {
    if (!line) return;
    setDismissedPackTips((prev) => ({ ...prev, [String(line.product_id)]: true }));
    if (line.supy_item_id && locationId != null) {
      postPackChoiceEvent({
        unified_location_id: locationId,
        item_id: line.supy_item_id,
        order_date: selectedOrderDate || undefined,
        event: "dismiss",
        suggested_packaging_id: tip?.packaging_id || null,
        horizon_days: tip?.horizon_days ?? null,
        base_need: line.base_need_qty ?? null,
      });
    }
  }

  function setLineQty(productId, qty) {
    if (qty === "" || qty == null) {
      setOrderQtys((prev) => ({ ...prev, [productId]: "" }));
      return;
    }
    const n = Math.max(0, Number(qty) || 0);
    setOrderQtys((prev) => ({ ...prev, [productId]: n }));
  }

  function resetSupplierQtys(supplier) {
    setOrderQtys((prev) => {
      const next = { ...prev };
      (supplier.lines || []).forEach((line) => {
        next[line.product_id] = procureiqInitialOrderQty(line.suggested_order_qty);
      });
      return next;
    });
  }

  async function handleOverrideMinimum(supplierId) {
    if (!locationId || actionBusy) return;
    setActionBusy(true);
    try {
      const res = await fetch(`${SHIFTIQ_API}/api/procureiq/override-supplier-minimum`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          unified_location_id: locationId,
          supplier_id: supplierId,
          reason: "",
          created_by: user.name,
        }),
      });
      if (!res.ok) throw new Error(await procureiqApiError(res, "Override failed"));
      loadTodayOrders({ force: true });
    } catch (err) {
      showProcureToast(err?.message || (lang === "gr" ? "Σφάλμα" : "Error"));
    } finally {
      setActionBusy(false);
    }
  }

  async function handleTopUpAdd(candidate) {
    if (!locationId || !candidate || actionBusy) return;
    setActionBusy(true);
    try {
      const res = await fetch(`${SHIFTIQ_API}/api/procureiq/override`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          unified_location_id: locationId,
          product_id: candidate.product_id,
          override_qty: candidate.case_size,
          created_by: user.name,
        }),
      });
      if (!res.ok) throw new Error(await procureiqApiError(res, "Top-up failed"));
      setTopUpModal(null);
      setTopUpData(null);
      loadTodayOrders({ force: true });
    } catch (err) {
      showProcureToast(err?.message || (lang === "gr" ? "Σφάλμα" : "Error"));
    } finally {
      setActionBusy(false);
    }
  }

  async function handleSendAll() {
    if (!locationId || sendBlocked || sendBusy || !canSendSelectedDay) return;
    const toSend = suppliers
      .map((supplier) => {
        const lines = (supplier.lines || [])
          .map((line) => ({
            product_id: line.product_id,
            final_qty: procureiqEditedQtyForTotal(orderQtys, line.product_id),
            packaging_id: line.selected_package_id || null,
          }))
          .filter((l) => l.final_qty > 0);
        return lines.length ? { supplier, lines } : null;
      })
      .filter(Boolean);

    if (!toSend.length) {
      showProcureToast(lang === "gr" ? "Δεν υπάρχουν γραμμές παραγγελίας" : "No order lines to send");
      return;
    }

    setSendBusy(true);
    setSendNotice(null);
    const failures = [];
    let draftCount = 0;
    try {
      for (const item of toSend) {
        setSendingSupplierId(item.supplier.supplier_id);
        try {
          const res = await fetch(`${SHIFTIQ_API}/api/procureiq/send-order`, {
            method: "POST",
            headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
            body: JSON.stringify({
              unified_location_id: locationId,
              supplier_id: item.supplier.supplier_id,
              lines: item.lines,
              sent_by: user.name,
            }),
          });
          if (!res.ok) throw new Error(await procureiqApiError(res, "Send failed"));
          const result = await res.json();
          if (result?.status === "draft") draftCount += 1;
        } catch (err) {
          failures.push({
            name: procureiqDisplaySupplierName(item.supplier.supplier_name),
            message: err?.message || (lang === "gr" ? "Αποτυχία" : "Failed"),
          });
        }
      }

      if (failures.length === 0) {
        setSendNotice(
          draftCount > 0
            ? t(
                `${draftCount} order${draftCount === 1 ? "" : "s"} sent as drafts`,
                `${draftCount} παραγγελία${draftCount === 1 ? "" : "ές"} ως πρόχειρα`
              )
            : t(
                `${toSend.length} order${toSend.length === 1 ? "" : "s"} sent`,
                `${toSend.length} παραγγελία${toSend.length === 1 ? "" : "ές"} εστάλησαν`
              )
        );
        loadTodayOrders({ force: true });
      } else if (draftCount > 0 || failures.length < toSend.length) {
        const sentOk = toSend.length - failures.length;
        setSendNotice(
          t(
            `${sentOk} sent, ${failures.length} failed: ${failures.map((f) => f.name).join(", ")}`,
            `${sentOk} εστάλησαν, ${failures.length} απέτυχαν: ${failures.map((f) => f.name).join(", ")}`
          )
        );
        loadTodayOrders({ force: true });
      } else {
        setSendNotice(
          t(
            `Send failed: ${failures.map((f) => `${f.name} (${f.message})`).join("; ")}`,
            `Αποτυχία αποστολής: ${failures.map((f) => `${f.name} (${f.message})`).join("; ")}`
          )
        );
      }
    } finally {
      setSendingSupplierId(null);
      setSendBusy(false);
    }
  }

  function scrollDayStrip(dir) {
    const el = dayStripRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 120, behavior: "smooth" });
  }

  function handleDayPillClick(idx) {
    const pill = dayPills[idx];
    if (!pill?.orderDate) return;
    setSelectedOrderDate(pill.orderDate);
    setDismissedPackTips({});
    setExpandedSuppliers({});
    setShowSuggestedSuppliers(true);
    setShowNoOrderSuppliers(false);
  }

  function mergeDeliverySlotsFromApi(apiSlots) {
    const byDay = {};
    (apiSlots || []).forEach((s) => {
      const day = String(s?.cutoff_day || "").toLowerCase();
      if (day) byDay[day] = s;
    });
    return WEEKDAY_SLOT_OPTIONS.map((d) => {
      const s = byDay[d];
      return {
        is_active: s ? s.is_active !== false : false,
        cutoff_day: d,
        cutoff_time: s?.cutoff_time || "23:59",
        delivery_day: s?.delivery_day || d,
      };
    });
  }

  function openSupplierEdit(supplier) {
    setEditingSupplierId(supplier.supplier_id);
    setSupplierSaveError(null);
    setScheduleCopyFromId("");
    setScheduleCopyToIds([]);
    setSupplierEditForm({
      safety_buffer_pct: Number(supplier.safety_buffer_pct) || 0,
      min_order_eur: Number(supplier.min_order_eur) || 0,
      contact_email: supplier.contact_email || "",
      contact_name: supplier.contact_name || "",
      phone: supplier.phone || "",
      delivery_slots: mergeDeliverySlotsFromApi(supplier.delivery_slots),
    });
  }

  function cancelSupplierEdit() {
    setEditingSupplierId(null);
    setSupplierEditForm(null);
    setSupplierSaveError(null);
    setScheduleCopyFromId("");
    setScheduleCopyToIds([]);
  }

  function updateSupplierEditField(field, value) {
    setSupplierEditForm((prev) => (prev ? { ...prev, [field]: value } : prev));
  }

  const scheduleCopyLocations = useMemo(() => {
    const brandId = selectedLocation?.brand_id;
    return (activeLocations || []).filter((loc) => {
      if (Number(loc.unified_location_id) === Number(locationId)) return false;
      if (brandId == null || brandId === "") return true;
      return Number(loc.brand_id) === Number(brandId);
    });
  }, [activeLocations, locationId, selectedLocation?.brand_id]);

  async function handleScheduleCopyFrom(supplierId) {
    const fromId = Number(scheduleCopyFromId);
    if (!fromId || !supplierId || scheduleCopyBusy) return;
    setScheduleCopyBusy(true);
    setSupplierSaveError(null);
    try {
      const brandId = selectedLocation?.brand_id || 15;
      const res = await fetch(
        `${SHIFTIQ_API}/api/procureiq/suppliers/${supplierId}/delivery-schedule` +
          `?location_id=${fromId}&brand_id=${brandId}`,
        { headers: getAuthHeaders() }
      );
      if (!res.ok) throw new Error(await procureiqApiError(res, "Copy from failed"));
      const data = await res.json();
      updateSupplierEditField("delivery_slots", mergeDeliverySlotsFromApi(data.slots));
      showProcureToast(
        lang === "gr" ? "Το πρόγραμμα αντιγράφηκε στη φόρμα" : "Schedule copied into form"
      );
    } catch (err) {
      setSupplierSaveError(err?.message || (lang === "gr" ? "Σφάλμα" : "Error"));
    } finally {
      setScheduleCopyBusy(false);
    }
  }

  async function handleScheduleCopyTo(supplierId) {
    const targets = scheduleCopyToIds.map(Number).filter((id) => id && id !== Number(locationId));
    if (!targets.length || !locationId || !supplierEditForm || scheduleCopyBusy) return;
    setScheduleCopyBusy(true);
    setSupplierSaveError(null);
    try {
      const brandId = selectedLocation?.brand_id || 15;
      const putRes = await fetch(
        `${SHIFTIQ_API}/api/procureiq/suppliers/${supplierId}/delivery-schedule`,
        {
          method: "PUT",
          headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({
            unified_location_id: locationId,
            brand_id: brandId,
            slots: (supplierEditForm.delivery_slots || []).filter((s) => s.is_active),
          }),
        }
      );
      if (!putRes.ok) throw new Error(await procureiqApiError(putRes, "Schedule save failed"));
      const copyRes = await fetch(
        `${SHIFTIQ_API}/api/procureiq/suppliers/${supplierId}/delivery-schedule/copy`,
        {
          method: "POST",
          headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({
            source_unified_location_id: locationId,
            target_unified_location_ids: targets,
            brand_id: brandId,
          }),
        }
      );
      if (!copyRes.ok) throw new Error(await procureiqApiError(copyRes, "Copy to failed"));
      setScheduleCopyToIds([]);
      showProcureToast(
        lang === "gr"
          ? `Αντιγράφηκε σε ${targets.length} κατάστημα(τα)`
          : `Copied to ${targets.length} store(s)`
      );
    } catch (err) {
      setSupplierSaveError(err?.message || (lang === "gr" ? "Σφάλμα" : "Error"));
    } finally {
      setScheduleCopyBusy(false);
    }
  }

  function dropBusyKey(itemId, productId, productName) {
    return `${locationId}:${itemId || productId || productName || ""}`;
  }

  function patchLeafLineAfterDropAction(itemId, patch) {
    const iid = String(itemId || "").trim();
    if (!iid) return;
    setForecastLeaves((prev) => {
      if (!prev?.lines) return prev;
      const lines = prev.lines.map((line) => {
        if (String(line.item_id || "").trim() !== iid) return line;
        return { ...line, ...patch };
      });
      const order_issue_counts = {};
      lines.forEach((line) => {
        if (!line.order_issue) return;
        order_issue_counts[line.order_issue] =
          (order_issue_counts[line.order_issue] || 0) + 1;
      });
      return { ...prev, lines, order_issue_counts };
    });
    setOrderBuild((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      if (Array.isArray(prev.lines)) {
        next.lines = prev.lines.map((line) => {
          if (String(line.item_id || "").trim() !== iid) return line;
          return { ...line, ...patch };
        });
      }
      if (prev.drops?.samples) {
        const samples = { ...prev.drops.samples };
        Object.keys(samples).forEach((reason) => {
          samples[reason] = (samples[reason] || []).filter((row) => {
            const rowId =
              typeof row === "object" && row
                ? String(row.itemId || row.item_id || "").trim()
                : "";
            return rowId !== iid;
          });
        });
        const counts = { ...(prev.drops.counts || {}) };
        Object.keys(samples).forEach((reason) => {
          counts[reason] = (samples[reason] || []).length;
          if (!counts[reason]) {
            delete counts[reason];
            delete samples[reason];
          }
        });
        next.drops = {
          ...prev.drops,
          samples,
          counts,
          total: Object.values(counts).reduce((a, b) => a + Number(b || 0), 0),
        };
      }
      return next;
    });
  }

  function afterDropActionSuccess(itemId, patch) {
    patchLeafLineAfterDropAction(itemId, patch);
    invalidateWeekOrdersCache(locationId, weekStart || "");
    // Background refresh without blocking modal.
    loadOrderBuild({ force: true, quiet: true });
  }

  async function handleItemStatus({ itemId, status, productName }) {
    const iid = String(itemId || "").trim();
    if (!locationId || !iid || dropActionBusyKey) return;
    const busyKey = dropBusyKey(iid, null, productName);
    setDropActionBusyKey(busyKey);
    try {
      const res = await fetch(`${SHIFTIQ_API}/api/procureiq/item-status`, {
        method: "PUT",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          unified_location_id: locationId,
          item_id: iid,
          status: status || null,
          updated_by: user.name || "ops",
          note: productName ? String(productName).slice(0, 200) : null,
        }),
      });
      if (!res.ok) throw new Error(await procureiqApiError(res, "Status update failed"));
      const label =
        status === "keep"
          ? lang === "gr"
            ? "Keep — θα παραγγέλνει παρά το ήσυχο ledger"
            : "Kept — will order despite quiet ledger"
          : status === "discontinued"
            ? lang === "gr"
              ? "Σημειώθηκε discontinued"
              : "Marked discontinued"
            : lang === "gr"
              ? "Το status καθαρίστηκε"
              : "Status cleared";
      showProcureToast(label);
      const patch =
        status === "keep"
          ? {
              order_issue: null,
              order_issue_label: null,
              order_issue_fix_hint: null,
              item_status: "keep",
            }
          : status === "discontinued"
            ? {
                order_issue: "discontinued",
                order_issue_label: "Discontinued",
                item_status: "discontinued",
              }
            : {
                order_issue: null,
                order_issue_label: null,
                order_issue_fix_hint: null,
                item_status: null,
              };
      afterDropActionSuccess(iid, patch);
    } catch (err) {
      showProcureToast(err?.message || (lang === "gr" ? "Σφάλμα" : "Error"));
    } finally {
      setDropActionBusyKey(null);
    }
  }

  async function handleEnableStoreProduct({ itemId, productId, productName }) {
    const iid = String(itemId || "").trim();
    const pid = productId != null && productId !== "" ? Number(productId) : null;
    if (!locationId || dropActionBusyKey) return;
    if (!iid && !Number.isFinite(pid)) return;
    const busyKey = dropBusyKey(iid, pid, productName);
    setDropActionBusyKey(busyKey);
    try {
      const res = await fetch(`${SHIFTIQ_API}/api/procureiq/store-product-config`, {
        method: "PUT",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          unified_location_id: locationId,
          item_id: iid || null,
          product_id: Number.isFinite(pid) ? pid : null,
          updated_by: user.name || "ops",
        }),
      });
      if (!res.ok) throw new Error(await procureiqApiError(res, "Enable failed"));
      showProcureToast(
        lang === "gr" ? "Ενεργοποιήθηκε στο κατάστημα" : "Enabled at this store"
      );
      afterDropActionSuccess(iid, {
        order_issue: null,
        order_issue_label: null,
        order_issue_fix_hint: null,
      });
    } catch (err) {
      showProcureToast(err?.message || (lang === "gr" ? "Σφάλμα" : "Error"));
    } finally {
      setDropActionBusyKey(null);
    }
  }

  async function handleClearExclude({ itemId, productId, productName }) {
    const iid = String(itemId || "").trim();
    const pid = productId != null && productId !== "" ? Number(productId) : null;
    if (!locationId || dropActionBusyKey) return;
    if (!iid && !Number.isFinite(pid)) return;
    const busyKey = dropBusyKey(iid, pid, productName);
    setDropActionBusyKey(busyKey);
    try {
      const res = await fetch(`${SHIFTIQ_API}/api/procureiq/clear-exclude`, {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          unified_location_id: locationId,
          item_id: iid || null,
          product_id: Number.isFinite(pid) ? pid : null,
          cleared_by: user.name || "ops",
        }),
      });
      if (!res.ok) throw new Error(await procureiqApiError(res, "Clear exclude failed"));
      const data = await res.json().catch(() => ({}));
      if (data.cleared) {
        showProcureToast(lang === "gr" ? "Το exclude καθαρίστηκε" : "Exclude cleared");
        afterDropActionSuccess(iid, {
          order_issue: null,
          order_issue_label: null,
          order_issue_fix_hint: null,
          excluded: false,
        });
      } else {
        showProcureToast(
          lang === "gr" ? "Δεν βρέθηκε exclude για καθαρισμό" : "No exclude row to clear"
        );
      }
    } catch (err) {
      showProcureToast(err?.message || (lang === "gr" ? "Σφάλμα" : "Error"));
    } finally {
      setDropActionBusyKey(null);
    }
  }

  async function handleSaveSupplier(supplierId) {
    if (!locationId || !supplierEditForm || supplierSaveBusy) return;
    setSupplierSaveBusy(true);
    setSupplierSaveError(null);
    try {
      const res = await fetch(`${SHIFTIQ_API}/api/procureiq/suppliers/${supplierId}`, {
        method: "PATCH",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          unified_location_id: locationId,
          min_order_eur: Number(supplierEditForm.min_order_eur),
          contact_email: supplierEditForm.contact_email || null,
          contact_name: supplierEditForm.contact_name || null,
          phone: supplierEditForm.phone || null,
          safety_buffer_pct: Number(supplierEditForm.safety_buffer_pct),
        }),
      });
      if (!res.ok) throw new Error(await procureiqApiError(res, "Save failed"));
      const schedRes = await fetch(
        `${SHIFTIQ_API}/api/procureiq/suppliers/${supplierId}/delivery-schedule`,
        {
          method: "PUT",
          headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({
            unified_location_id: locationId,
            brand_id: selectedLocation?.brand_id || 15,
            slots: (supplierEditForm.delivery_slots || []).filter((s) => s.is_active),
          }),
        }
      );
      if (!schedRes.ok) throw new Error(await procureiqApiError(schedRes, "Schedule save failed"));
      cancelSupplierEdit();
      setSupplierSavedId(supplierId);
      if (supplierSavedTimerRef.current) clearTimeout(supplierSavedTimerRef.current);
      supplierSavedTimerRef.current = setTimeout(() => setSupplierSavedId(null), 2200);
      loadSuppliersAdmin();
    } catch (err) {
      setSupplierSaveError(err?.message || (lang === "gr" ? "Σφάλμα" : "Error"));
    } finally {
      setSupplierSaveBusy(false);
    }
  }

  const filteredSuppliersAdmin = useMemo(() => {
    const list = suppliersAdmin?.suppliers || [];
    const q = suppliersSearch.trim().toLowerCase();
    if (!q) return list;
    return list.filter((s) =>
      procureiqDisplaySupplierName(s.name).toLowerCase().includes(q)
    );
  }, [suppliersAdmin, suppliersSearch]);

  const t = (en, gr) => (lang === "gr" ? gr : en);

  const showLocationLoadingModal =
    activeTab === "consumption"
      ? consumptionLoading || consumptionRefreshBusy
      : activeTab === "today" || activeTab === "week"
      ? loading
      : activeTab === "history"
        ? orderHistoryLoading
        : activeTab === "suppliers"
          ? suppliersAdminLoading
          : activeTab === "forecast-menu"
            ? forecastMenuLoading
            : activeTab === "forecast-leaves"
              ? forecastLeavesLoading
              : activeTab === "forecast-contrib"
                ? forecastContribLoading
                : activeTab === "order-build" || activeTab === "order-analysis"
                  ? orderBuildLoading
                  : false;

  const showWeekPicker = procureiqForecastTabsNeedWeek(activeTab);
  const consumptionLatestIso = procureiqConsumptionLatestIsoDate();
  const consumptionIsLatest = consumptionDate === consumptionLatestIso;

  const selectedStoreLabel = canPickStore
    ? procureiqLocationWithBrandLabel(selectedLocation)
    : procureiqStoreWithBrandLabel(lockedStoreName || "", storeRecords);
  const selectedStoreDotColor = canPickStore
    ? procureiqLocationDotColor(selectedLocation)
    : procureiqBrandDotColor(lockedStoreName || "", storeRecords);
  const showPickerButton = canPickStore && activeLocations.length > 1;

  const procureHeader = (
    <>
      {showPickerButton ? (
        <button
          type="button"
          className="procureiq-store-btn"
          onClick={() => setShowStorePicker(true)}
        >
          <span style={{ width: 10, height: 10, borderRadius: "50%", background: selectedStoreDotColor, flexShrink: 0 }} />
          {selectedStoreLabel}
          <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--text-muted)" }}>▾</span>
        </button>
      ) : (
        <div className="info-pill" style={{ marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 10, height: 10, borderRadius: "50%", background: selectedStoreDotColor, flexShrink: 0 }} />
          {selectedStoreLabel || (locationsLoading ? t("Loading…", "Φόρτωση…") : t("No location", "Καμία τοποθεσία"))}
        </div>
      )}

      <div className="procureiq-tabs">
        <button
          type="button"
          className={`procureiq-tab ${activeTab === "consumption" ? "procureiq-tab-active" : ""}`}
          onClick={() => setActiveTab("consumption")}
          title={t(
            "Actual sales × recipe: raw material use for a day. Audit only.",
            "Πραγματικές πωλήσεις × συνταγή: κατανάλωση πρώτων υλών ανά ημέρα. Μόνο audit."
          )}
        >
          {t("Consumption", "Κατανάλωση")}
        </button>
        <button
          type="button"
          className={`procureiq-tab ${activeTab === "today" ? "procureiq-tab-active" : ""}`}
          onClick={() => setActiveTab("today")}
        >
          {t("Today", "Σήμερα")}
        </button>
        <button
          type="button"
          className={`procureiq-tab ${activeTab === "week" ? "procureiq-tab-active" : ""}`}
          onClick={() => setActiveTab("week")}
        >
          {t("This Week", "Αυτή η Εβδομάδα")}
        </button>
        <button
          type="button"
          className={`procureiq-tab ${activeTab === "history" ? "procureiq-tab-active" : ""}`}
          onClick={() => setActiveTab("history")}
        >
          {t("Order History", "Ιστορικό Παραγγελιών")}
        </button>
        {canManageSuppliers && (
          <button
            type="button"
            className={`procureiq-tab ${activeTab === "suppliers" ? "procureiq-tab-active" : ""}`}
            onClick={() => setActiveTab("suppliers")}
          >
            {t("Suppliers", "Προμηθευτές")}
          </button>
        )}
        {canSeeForecastTrace && (
          <>
            <button
              type="button"
              className={`procureiq-tab ${activeTab === "forecast-leaves" ? "procureiq-tab-active" : ""}`}
              onClick={() => setActiveTab("forecast-leaves")}
            >
              {t("Raw materials", "Πρώτες ύλες")}
            </button>
            <button
              type="button"
              className={`procureiq-tab ${activeTab === "order-build" ? "procureiq-tab-active" : ""}`}
              onClick={() => setActiveTab("order-build")}
            >
              {t("Order build", "Κατασκευή παραγγελίας")}
            </button>
            <button
              type="button"
              className={`procureiq-tab ${activeTab === "forecast-menu" ? "procureiq-tab-active" : ""}`}
              onClick={() => setActiveTab("forecast-menu")}
              title={t(
                "Sold units from procureiq_forecast_material_w. Ordering uses Raw materials (leaf_w).",
                "Πωλημένες μονάδες από material_w. Παραγγελία από Πρώτες ύλες (leaf_w)."
              )}
              style={
                forecastMenu && !forecastMenu.available
                  ? { opacity: 0.55 }
                  : undefined
              }
            >
              {t("Menu hats", "Hats μενού")}
            </button>
            <button
              type="button"
              className={`procureiq-tab ${activeTab === "forecast-contrib" ? "procureiq-tab-active" : ""}`}
              onClick={() => setActiveTab("forecast-contrib")}
              title={t(
                "Audit only: which sold SKUs drove each raw material. Not used for ordering.",
                "Μόνο audit: ποια sold SKUs οδήγησαν κάθε πρώτη ύλη. Δεν χρησιμοποιείται για παραγγελία."
              )}
            >
              {t("Leaf sources", "Πηγές πρώτων υλών")}
            </button>
            <button
              type="button"
              className={`procureiq-tab ${activeTab === "order-analysis" ? "procureiq-tab-active" : ""}`}
              onClick={() => setActiveTab("order-analysis")}
            >
              {t("Order analysis", "Ανάλυση παραγγελίας")}
            </button>
          </>
        )}
      </div>
      {showWeekPicker && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 12 }}>
          <WeekPicker
            weekStartISO={weekStart}
            goLiveWeek={PROCUREIQ_WEEK_PICKER_FLOOR}
            lang={lang}
            onCommit={commitProcureWeek}
          />
          <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600 }}>
            {t("Forecast / order week", "Εβδομάδα πρόβλεψης / παραγγελίας")}
          </span>
        </div>
      )}
      {activeTab === "consumption" && (
        <div className="procureiq-day-picker">
          <button
            type="button"
            className="procureiq-day-arrow"
            aria-label={t("Previous day", "Προηγούμενη ημέρα")}
            onClick={() => {
              if (!consumptionDate) return;
              setConsumptionDate(procureiqShiftIsoDate(consumptionDate, -1));
            }}
          >
            ‹
          </button>
          <label className="procureiq-day-picker-field">
            <span className="procureiq-day-picker-caption">
              {t("Day", "Ημέρα")}
              <strong>{procureiqFormatDayLabel(consumptionDate, lang)}</strong>
            </span>
            <input
              type="date"
              className="form-input procureiq-day-picker-input"
              value={consumptionDate || ""}
              max={consumptionLatestIso}
              onChange={(e) => {
                const v = e.target.value;
                if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return;
                setConsumptionDate(v > consumptionLatestIso ? consumptionLatestIso : v);
              }}
            />
          </label>
          <button
            type="button"
            className="procureiq-day-arrow"
            aria-label={t("Next day", "Επόμενη ημέρα")}
            disabled={consumptionIsLatest || !consumptionDate}
            onClick={() => {
              if (!consumptionDate || consumptionIsLatest) return;
              const next = procureiqShiftIsoDate(consumptionDate, 1);
              setConsumptionDate(next > consumptionLatestIso ? consumptionLatestIso : next);
            }}
          >
            ›
          </button>
          {!consumptionIsLatest ? (
            <button
              type="button"
              className="filter-chip"
              onClick={() => setConsumptionDate(consumptionLatestIso)}
            >
              {t("Yesterday", "Χθες")}
            </button>
          ) : null}
        </div>
      )}
    </>
  );

  return (
    <div className="anim-fade">
      {toast && (
        <div
          role="status"
          style={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 3000,
            background: "var(--surface)",
            color: "var(--text-primary)",
            border: "1.5px solid var(--border)",
            borderRadius: 10,
            padding: "10px 16px",
            fontSize: 13,
            fontWeight: 700,
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            whiteSpace: "nowrap",
          }}
        >
          {toast}
        </div>
      )}

      {procureHeader}

      {showLocationLoadingModal && (
        <div
          className="modal-overlay"
          style={{ zIndex: 2500 }}
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div
            className="modal-box"
            style={{
              maxWidth: activeTab === "consumption" ? 360 : 280,
              textAlign: "center",
              padding: "32px 24px",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <LoadingPulse size={32} />
            <p style={{ marginTop: 16, fontWeight: 700, fontSize: 14, color: "var(--text-primary)" }}>
              {activeTab === "consumption"
                ? consumptionProgress?.message ||
                  t("Building consumption…", "Υπολογισμός κατανάλωσης…")
                : t("Loading store…", "Φόρτωση καταστήματος…")}
            </p>
            {activeTab === "consumption" && (
              <>
                {consumptionProgress?.detail ? (
                  <p
                    style={{
                      marginTop: 6,
                      fontSize: 12,
                      color: "var(--text-muted)",
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    }}
                  >
                    {consumptionProgress.detail}
                  </p>
                ) : null}
                <div
                  style={{
                    marginTop: 14,
                    height: 8,
                    borderRadius: 999,
                    background: "var(--border, #e5e7eb)",
                    overflow: "hidden",
                    textAlign: "left",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${Math.max(4, Math.min(100, Number(consumptionProgress?.pct) || 4))}%`,
                      background: "var(--brand, #16a34a)",
                      transition: "width 0.35s ease",
                    }}
                  />
                </div>
                <p style={{ marginTop: 8, fontSize: 12, color: "var(--text-muted)" }}>
                  {consumptionProgress?.elapsed_sec != null
                    ? t(
                        `${Math.round(consumptionProgress.elapsed_sec)}s · first load builds the day cache`,
                        `${Math.round(consumptionProgress.elapsed_sec)}δ · η πρώτη φόρτωση χτίζει την cache`
                      )
                    : t(
                        "First load builds the day cache — can take a minute",
                        "Η πρώτη φόρτωση χτίζει την cache — μπορεί να πάρει ένα λεπτό"
                      )}
                </p>
              </>
            )}
            <button
              type="button"
              className="btn-base btn-outline btn-small"
              style={{ marginTop: 16 }}
              onClick={cancelLocationLoad}
            >
              {t("Cancel", "Ακύρωση")}
            </button>
          </div>
        </div>
      )}

      {activeTab === "consumption" && consumptionError && (
        <div className="card-box" style={{ marginBottom: 14 }}>
          <p style={{ fontWeight: 700, color: "var(--danger, #b91c1c)" }}>{consumptionError}</p>
          <button
            type="button"
            className="btn-base btn-outline btn-small"
            onClick={() => loadConsumptionDay()}
          >
            {t("Retry", "Επανάληψη")}
          </button>
        </div>
      )}
      {activeTab === "consumption" && !consumptionError && consumptionDay && !consumptionLoading && (
        <ConsumptionDayPanel
          data={consumptionDay}
          t={t}
          locationId={locationId}
          businessDate={consumptionDate}
          view={consumptionView}
          onViewChange={setConsumptionView}
          canRefresh={consumptionIsLatest}
          refreshBusy={consumptionRefreshBusy}
          onRefresh={() => loadConsumptionDay({ forceRefresh: true })}
        />
      )}
      {activeTab === "consumption" && !locationId && !locationsLoading && (
        <div className="card-box">
          <p style={{ fontSize: 14, fontWeight: 700 }}>
            {t("Select a store to view consumption.", "Επιλέξτε κατάστημα για κατανάλωση.")}
          </p>
        </div>
      )}

      {activeTab === "history" && orderHistoryError && (
        <div className="card-box" style={{ borderColor: "#FACFC8", background: "#FEF0ED", marginBottom: 14 }}>
          <p style={{ fontWeight: 800, fontSize: 14, color: "var(--danger)", marginBottom: 6 }}>
            {t("Failed to load order history", "Αποτυχία φόρτωσης ιστορικού")}
          </p>
          <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 12 }}>{orderHistoryError}</p>
          <button type="button" className="btn-base btn-outline btn-small" onClick={loadOrderHistory}>
            {t("Retry", "Δοκιμάστε ξανά")}
          </button>
        </div>
      )}

      {activeTab === "history" && !orderHistoryError && orderHistory && !orderHistoryLoading && (
        <div className="card-box procureiq-send-block">
          {(orderHistory.orders || []).length === 0 ? (
            <div style={{ textAlign: "center", padding: "24px 12px" }}>
              <p style={{ fontSize: 14, fontWeight: 700, color: "var(--text-primary)", marginBottom: 6 }}>
                {t("No orders sent yet.", "Δεν έχουν σταλεί παραγγελίες ακόμα.")}
              </p>
              <p style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5 }}>
                {t("Orders you send will appear here.", "Οι παραγγελίες που στέλνετε θα εμφανίζονται εδώ.")}
              </p>
            </div>
          ) : (
            (orderHistory.orders || []).map((order) => {
              const displayName = procureiqDisplaySupplierName(order.supplier_name);
              const lineCount = Number(order.line_count) || 0;
              const isSent = order.status === "sent";
              return (
                <div
                  key={order.order_id}
                  className="procureiq-supplier-row procureiq-supplier-row-readonly"
                >
                  <div
                    className="procureiq-supplier-avatar"
                    style={{ background: procureiqColorFromName(order.supplier_name) }}
                  >
                    {procureiqSupplierInitial(order.supplier_name)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: 13, color: "var(--text-primary)" }}>
                      {displayName}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                      {lineCount} {lineCount === 1 ? t("item", "είδος") : t("items", "είδη")}
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0, textAlign: "center", padding: "0 8px" }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)" }}>
                      {order.sent_by || "—"}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                      {procureiqFormatOrderHistoryDate(order.created_at, lang)}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    {Number(order.total_eur) > 0 ? (
                      <div style={{ fontWeight: 800, fontSize: 13, color: "var(--text-primary)" }}>
                        {formatProcureEuro(order.total_eur)}
                      </div>
                    ) : null}
                    <span
                      className={`procureiq-badge ${
                        isSent ? "procureiq-badge-ready" : "procureiq-badge-draft"
                      }`}
                      style={{ marginTop: 4, display: "inline-block" }}
                    >
                      {isSent ? t("SENT", "ΕΣΤΑΛΗ") : t("DRAFT", "ΠΡΟΧΕΙΡΟ")}
                    </span>
                    {order.is_override && (
                      <span className="procureiq-override-label">{t("MIN OVERRIDE", "MIN OVERRIDE")}</span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {canManageSuppliers && activeTab === "suppliers" && suppliersAdminError && (
        <div className="card-box" style={{ borderColor: "#FACFC8", background: "#FEF0ED", marginBottom: 14 }}>
          <p style={{ fontWeight: 800, fontSize: 14, color: "var(--danger)", marginBottom: 6 }}>
            {t("Failed to load suppliers", "Αποτυχία φόρτωσης προμηθευτών")}
          </p>
          <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 12 }}>{suppliersAdminError}</p>
          <button type="button" className="btn-base btn-outline btn-small" onClick={loadSuppliersAdmin}>
            {t("Retry", "Δοκιμάστε ξανά")}
          </button>
        </div>
      )}

      {canSeeForecastTrace && activeTab === "forecast-menu" && forecastMenuError && (
        <div className="card-box" style={{ borderColor: "#FACFC8", background: "#FEF0ED", marginBottom: 14 }}>
          <p style={{ fontWeight: 800, fontSize: 14, color: "var(--danger)", marginBottom: 6 }}>
            {t("Failed to load forecast menu", "Αποτυχία φόρτωσης πρόβλεψης μενού")}
          </p>
          <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 12 }}>{forecastMenuError}</p>
          <button type="button" className="btn-base btn-outline btn-small" onClick={loadForecastMenu}>
            {t("Retry", "Δοκιμάστε ξανά")}
          </button>
        </div>
      )}
      {canSeeForecastTrace && activeTab === "forecast-menu" && !forecastMenuError && forecastMenu && !forecastMenuLoading && (
        <ForecastMenuPanel data={forecastMenu} t={t} />
      )}

      {canSeeForecastTrace && activeTab === "forecast-leaves" && forecastLeavesError && (
        <div className="card-box" style={{ borderColor: "#FACFC8", background: "#FEF0ED", marginBottom: 14 }}>
          <p style={{ fontWeight: 800, fontSize: 14, color: "var(--danger)", marginBottom: 6 }}>
            {t("Failed to load raw materials", "Αποτυχία φόρτωσης πρώτων υλών")}
          </p>
          <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 12 }}>{forecastLeavesError}</p>
          <button type="button" className="btn-base btn-outline btn-small" onClick={loadForecastLeaves}>
            {t("Retry", "Δοκιμάστε ξανά")}
          </button>
        </div>
      )}
      {canSeeForecastTrace && activeTab === "forecast-leaves" && !forecastLeavesError && forecastLeaves && !forecastLeavesLoading && (
        <ForecastLeavesPanel
          data={forecastLeaves}
          t={t}
          locationId={locationId}
          onItemStatus={handleItemStatus}
          onEnableStoreProduct={handleEnableStoreProduct}
          onClearExclude={handleClearExclude}
          dropActionBusyKey={dropActionBusyKey}
        />
      )}

      {canSeeForecastTrace && activeTab === "forecast-contrib" && forecastContribError && (
        <div className="card-box" style={{ borderColor: "#FACFC8", background: "#FEF0ED", marginBottom: 14 }}>
          <p style={{ fontWeight: 800, fontSize: 14, color: "var(--danger)", marginBottom: 6 }}>
            {t("Failed to load leaf sources", "Αποτυχία φόρτωσης πηγών πρώτων υλών")}
          </p>
          <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 12 }}>{forecastContribError}</p>
          <button type="button" className="btn-base btn-outline btn-small" onClick={loadForecastContrib}>
            {t("Retry", "Δοκιμάστε ξανά")}
          </button>
        </div>
      )}
      {canSeeForecastTrace && activeTab === "forecast-contrib" && !forecastContribError && forecastContrib && !forecastContribLoading && (
        <ForecastLeafContribPanel data={forecastContrib} t={t} />
      )}

      {canSeeForecastTrace && activeTab === "order-analysis" && !orderBuildError && (weekOrders || orderBuild) && !orderBuildLoading && (
        <OrderAnalysisPanel
          weekOrders={weekOrders}
          leaves={forecastLeaves}
          orderBuild={orderBuild}
          t={t}
          weekStart={weekStart}
        />
      )}

      {canSeeForecastTrace && activeTab === "order-build" && orderBuildError && (
        <div className="card-box" style={{ borderColor: "#FACFC8", background: "#FEF0ED", marginBottom: 14 }}>
          <p style={{ fontWeight: 800, fontSize: 14, color: "var(--danger)", marginBottom: 6 }}>
            {t("Failed to load order build", "Αποτυχία φόρτωσης κατασκευής παραγγελίας")}
          </p>
          <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 12 }}>{orderBuildError}</p>
          <button
            type="button"
            className="btn-base btn-outline btn-small"
            onClick={() => loadOrderBuild({ force: true })}
          >
            {t("Retry", "Δοκιμάστε ξανά")}
          </button>
        </div>
      )}
      {canSeeForecastTrace && activeTab === "order-build" && !orderBuildError && orderBuild && !orderBuildLoading && (
        <OrderBuildPanel
          data={orderBuild}
          t={t}
          meta={orderBuildMeta}
          onRefresh={() => loadOrderBuild({ force: true })}
          locationId={locationId}
          onItemStatus={handleItemStatus}
          onEnableStoreProduct={handleEnableStoreProduct}
          onClearExclude={handleClearExclude}
          dropActionBusyKey={dropActionBusyKey}
        />
      )}

      {canManageSuppliers && activeTab === "suppliers" && !suppliersAdminError && suppliersAdmin && !suppliersAdminLoading && (
        <>
          <div style={{ marginBottom: 12 }}>
            <input
              className="admin-search"
              style={{ width: "100%", maxWidth: "100%" }}
              value={suppliersSearch}
              onChange={(e) => setSuppliersSearch(e.target.value)}
              placeholder={t("Search suppliers…", "Αναζήτηση προμηθευτών…")}
            />
          </div>
          <div className="card-box procureiq-send-block">
            {filteredSuppliersAdmin.length === 0 ? (
              <div style={{ textAlign: "center", padding: "24px 12px" }}>
                <p style={{ fontSize: 14, fontWeight: 700, color: "var(--text-primary)" }}>
                  {suppliersSearch.trim()
                    ? t("No suppliers match your search", "Κανένας προμηθευτής δεν ταιριάζει")
                    : t("No suppliers configured for this location", "Δεν υπάρχουν προμηθευτές για αυτή την τοποθεσία")}
                </p>
              </div>
            ) : (
              filteredSuppliersAdmin.map((supplier) => {
                const displayName = procureiqDisplaySupplierName(supplier.name);
                const productCount = Number(supplier.product_count) || 0;
                const isEditing = editingSupplierId === supplier.supplier_id;
                const justSaved = supplierSavedId === supplier.supplier_id;
                const bufferPct = Number(supplier.safety_buffer_pct);
                const bufferLabel = Number.isFinite(bufferPct) ? Math.round(bufferPct * 10) / 10 : 0;
                const minEur = Number(supplier.min_order_eur);
                const minLabel = Number.isFinite(minEur) ? minEur.toFixed(minEur % 1 === 0 ? 0 : 2) : "0";
                return (
                  <div key={supplier.supplier_id}>
                    <div className="procureiq-supplier-row procureiq-supplier-row-readonly">
                      <div
                        className="procureiq-supplier-avatar"
                        style={{ background: procureiqColorFromName(supplier.name) }}
                      >
                        {procureiqSupplierInitial(supplier.name)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <div style={{ fontWeight: 800, fontSize: 13, color: "var(--text-primary)" }}>
                            {displayName}
                          </div>
                          <span className="procureiq-badge procureiq-badge-draft">
                            {productCount} {productCount === 1 ? t("product", "προϊόν") : t("products", "προϊόντα")}
                          </span>
                          {justSaved && (
                            <span style={{ fontSize: 12, fontWeight: 700, color: "#166534" }}>
                              {t("Saved ✓", "Αποθηκεύτηκε ✓")}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
                          {supplier.delivery_schedule_summary || t("No delivery schedule", "Χωρίς πρόγραμμα παράδοσης")}
                          {" · "}
                          {t("Buffer", "Buffer")} {bufferLabel}% · {t("Min", "Min")} €{minLabel}
                        </div>
                      </div>
                      {!isEditing && (
                        <button
                          type="button"
                          className="btn-base btn-outline btn-small"
                          onClick={() => openSupplierEdit(supplier)}
                        >
                          {t("Edit", "Επεξεργασία")}
                        </button>
                      )}
                    </div>
                    {isEditing && supplierEditForm && (
                      <div className="procureiq-expanded-lines" style={{ paddingTop: 12 }}>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 10,
                          }}
                        >
                          <div>
                            <label className="form-label">{t("Safety Buffer %", "Buffer %")}</label>
                            <input
                              type="number"
                              className="form-input"
                              min={0}
                              max={500}
                              value={supplierEditForm.safety_buffer_pct}
                              onChange={(e) => updateSupplierEditField("safety_buffer_pct", e.target.value)}
                            />
                            <p style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
                              {t("Applies to all stores", "Ισχύει για όλα τα καταστήματα")}
                            </p>
                          </div>
                          <div>
                            <label className="form-label">{t("Min Order €", "Ελάχιστη παραγγελία €")}</label>
                            <input
                              type="number"
                              className="form-input"
                              min={0}
                              max={9999}
                              step="0.01"
                              value={supplierEditForm.min_order_eur}
                              onChange={(e) => updateSupplierEditField("min_order_eur", e.target.value)}
                            />
                            <p style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
                              {t("Same for all stores", "Ίδιο για όλα τα καταστήματα")}
                            </p>
                          </div>
                          <div style={{ gridColumn: "1 / -1" }}>
                            <label className="form-label">
                              {t("Delivery schedule (cutoff → delivery)", "Πρόγραμμα παράδοσης (cutoff → παράδοση)")}
                            </label>
                            <p style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 8 }}>
                              {t(
                                "Per store. Use copy to share across locations.",
                                "Ανά κατάστημα. Χρησιμοποιήστε αντιγραφή για άλλα καταστήματα."
                              )}
                            </p>
                            {scheduleCopyLocations.length > 0 && (
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "1fr auto",
                                  gap: 8,
                                  marginBottom: 10,
                                  alignItems: "end",
                                }}
                              >
                                <div>
                                  <label className="form-label" style={{ fontSize: 11 }}>
                                    {t("Copy from store", "Αντιγραφή από κατάστημα")}
                                  </label>
                                  <select
                                    className="form-input"
                                    value={scheduleCopyFromId}
                                    disabled={scheduleCopyBusy || supplierSaveBusy}
                                    onChange={(e) => setScheduleCopyFromId(e.target.value)}
                                  >
                                    <option value="">
                                      {t("Select store…", "Επιλέξτε κατάστημα…")}
                                    </option>
                                    {scheduleCopyLocations.map((loc) => (
                                      <option
                                        key={`from-${loc.unified_location_id}-${loc.brand_id}`}
                                        value={loc.unified_location_id}
                                      >
                                        {procureiqLocationWithBrandLabel(loc)}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <button
                                  type="button"
                                  className="btn-base btn-outline btn-small"
                                  disabled={
                                    !scheduleCopyFromId || scheduleCopyBusy || supplierSaveBusy
                                  }
                                  onClick={() => handleScheduleCopyFrom(supplier.supplier_id)}
                                >
                                  {t("Copy from", "Αντιγραφή από")}
                                </button>
                                <div style={{ gridColumn: "1 / -1" }}>
                                  <label className="form-label" style={{ fontSize: 11 }}>
                                    {t("Copy to stores", "Αντιγραφή σε καταστήματα")}
                                  </label>
                                  <select
                                    className="form-input"
                                    multiple
                                    size={Math.min(4, Math.max(2, scheduleCopyLocations.length))}
                                    value={scheduleCopyToIds.map(String)}
                                    disabled={scheduleCopyBusy || supplierSaveBusy}
                                    onChange={(e) => {
                                      const selected = Array.from(e.target.selectedOptions).map(
                                        (o) => o.value
                                      );
                                      setScheduleCopyToIds(selected);
                                    }}
                                  >
                                    {scheduleCopyLocations.map((loc) => (
                                      <option
                                        key={`to-${loc.unified_location_id}-${loc.brand_id}`}
                                        value={loc.unified_location_id}
                                      >
                                        {procureiqLocationWithBrandLabel(loc)}
                                      </option>
                                    ))}
                                  </select>
                                  <button
                                    type="button"
                                    className="btn-base btn-outline btn-small"
                                    style={{ marginTop: 6 }}
                                    disabled={
                                      !scheduleCopyToIds.length ||
                                      scheduleCopyBusy ||
                                      supplierSaveBusy
                                    }
                                    onClick={() => handleScheduleCopyTo(supplier.supplier_id)}
                                  >
                                    {scheduleCopyBusy
                                      ? t("Copying…", "Αντιγραφή…")
                                      : t("Copy to selected", "Αντιγραφή στα επιλεγμένα")}
                                  </button>
                                </div>
                              </div>
                            )}
                            {(supplierEditForm.delivery_slots || []).map((slot, slotIdx) => (
                              <div
                                key={slot.cutoff_day}
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "auto 1fr 1fr 1fr",
                                  gap: 6,
                                  alignItems: "center",
                                  marginBottom: 6,
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={!!slot.is_active}
                                  onChange={(e) => {
                                    const next = [...(supplierEditForm.delivery_slots || [])];
                                    next[slotIdx] = { ...next[slotIdx], is_active: e.target.checked };
                                    updateSupplierEditField("delivery_slots", next);
                                  }}
                                />
                                <span style={{ fontSize: 12, textTransform: "capitalize" }}>{slot.cutoff_day}</span>
                                <input
                                  type="time"
                                  className="form-input"
                                  value={slot.cutoff_time || "23:59"}
                                  disabled={!slot.is_active}
                                  onChange={(e) => {
                                    const next = [...(supplierEditForm.delivery_slots || [])];
                                    next[slotIdx] = { ...next[slotIdx], cutoff_time: e.target.value };
                                    updateSupplierEditField("delivery_slots", next);
                                  }}
                                />
                                <select
                                  className="form-input"
                                  value={slot.delivery_day || slot.cutoff_day}
                                  disabled={!slot.is_active}
                                  onChange={(e) => {
                                    const next = [...(supplierEditForm.delivery_slots || [])];
                                    next[slotIdx] = { ...next[slotIdx], delivery_day: e.target.value };
                                    updateSupplierEditField("delivery_slots", next);
                                  }}
                                >
                                  {WEEKDAY_SLOT_OPTIONS.map((d) => (
                                    <option key={d} value={d}>
                                      {d}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            ))}
                          </div>
                          <div style={{ gridColumn: "1 / -1" }}>
                            <label className="form-label">{t("Contact Email", "Email επικοινωνίας")}</label>
                            <input
                              type="email"
                              className="form-input"
                              value={supplierEditForm.contact_email}
                              onChange={(e) => updateSupplierEditField("contact_email", e.target.value)}
                            />
                            <p style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
                              {t("Same for all stores", "Ίδιο για όλα τα καταστήματα")}
                            </p>
                          </div>
                          <div>
                            <label className="form-label">{t("Contact Name", "Όνομα επικοινωνίας")}</label>
                            <input
                              type="text"
                              className="form-input"
                              value={supplierEditForm.contact_name}
                              onChange={(e) => updateSupplierEditField("contact_name", e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="form-label">{t("Phone", "Τηλέφωνο")}</label>
                            <input
                              type="text"
                              className="form-input"
                              value={supplierEditForm.phone}
                              onChange={(e) => updateSupplierEditField("phone", e.target.value)}
                            />
                          </div>
                        </div>
                        {supplierSaveError && (
                          <p style={{ fontSize: 12, fontWeight: 600, color: "var(--danger)", marginTop: 10 }}>
                            {supplierSaveError}
                          </p>
                        )}
                        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                          <button
                            type="button"
                            className="btn-base btn-green btn-small"
                            disabled={supplierSaveBusy}
                            onClick={() => handleSaveSupplier(supplier.supplier_id)}
                          >
                            {supplierSaveBusy ? <LoadingPulse size={16} color="#fff" /> : t("Save", "Αποθήκευση")}
                          </button>
                          <button
                            type="button"
                            className="btn-base btn-outline btn-small"
                            disabled={supplierSaveBusy}
                            onClick={cancelSupplierEdit}
                          >
                            {t("Cancel", "Ακύρωση")}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </>
      )}

      {(activeTab === "today" || activeTab === "week") && loadError && (
        <div className="card-box" style={{ borderColor: "#FACFC8", background: "#FEF0ED", marginBottom: 14 }}>
          <p style={{ fontWeight: 800, fontSize: 14, color: "var(--danger)", marginBottom: 6 }}>
            {t("Failed to load orders", "Αποτυχία φόρτωσης παραγγελιών")}
          </p>
          <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 12 }}>{loadError}</p>
          <button
            type="button"
            className="btn-base btn-outline btn-small"
            onClick={() => loadTodayOrders({ force: true })}
          >
            {t("Retry", "Δοκιμάστε ξανά")}
          </button>
        </div>
      )}

      {(activeTab === "today" || activeTab === "week") && !loadError && todayOrders && !loading && (
        <>
          {todayOrders.is_leaf_forecast_week && (
            <div
              className="card-box"
              style={{
                borderColor: "#C5D9F0",
                background: "#F0F6FC",
                marginBottom: 14,
                padding: "12px 14px",
              }}
            >
              <p style={{ fontWeight: 800, fontSize: 14, marginBottom: 4 }}>
                {t(
                  "Leaf forecast week · recommend only",
                  "Εβδομάδα leaf forecast · μόνο πρόταση"
                )}
              </p>
              <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: 0 }}>
                {t(
                  `Week ${todayOrders.week_start || weekStart}. Schedule view · not sent to suppliers.`,
                  `Εβδομάδα ${todayOrders.week_start || weekStart}. Προβολή προγράμματος · δεν αποστέλλεται σε προμηθευτές.`
                )}
              </p>
              {weekOrders?.week_basket_eur != null && (
                <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: "6px 0 0" }}>
                  {t(
                    `Week suggested total ${formatProcureEuroCompact(weekOrders.week_basket_eur)}.`,
                    `Προτεινόμενο σύνολο εβδομάδας ${formatProcureEuroCompact(weekOrders.week_basket_eur)}.`
                  )}
                </p>
              )}
              {Number(todayOrders?.drops?.total || 0) > 0 && (
                <p style={{ fontSize: 13, color: "var(--danger)", margin: "8px 0 0" }}>
                  {t(
                    `${todayOrders.drops.total} forecast lines dropped (no channel price / package). Check API drops samples.`,
                    `${todayOrders.drops.total} γραμμές πρόβλεψης αποκλείστηκαν (χωρίς τιμή καναλιού / συσκευασία).`
                  )}
                </p>
              )}
            </div>
          )}
          <div className="procureiq-day-strip-wrap">
            <button
              type="button"
              className="procureiq-day-arrow"
              disabled={selectedDayIdx === 0}
              onClick={() => {
                if (selectedDayIdx > 0) handleDayPillClick(selectedDayIdx - 1);
                else scrollDayStrip(-1);
              }}
              aria-label="Previous"
              title={
                selectedDayIdx === 0
                  ? t("Already on first day", "Ήδη στην πρώτη μέρα")
                  : t("Previous day", "Προηγούμενη μέρα")
              }
            >
              ←
            </button>
            <div className="procureiq-day-strip" ref={dayStripRef}>
              {dayPills.map((pill) => {
                const isActive = pill.idx === selectedDayIdx;
                const locale = lang === "gr" ? "el-GR" : "en-GB";
                const dayName = pill.isToday
                  ? t("Today", "Σήμερα")
                  : pill.date.toLocaleDateString(locale, { weekday: "short" });
                const dateLabel = pill.date.toLocaleDateString(locale, {
                  day: "numeric",
                  month: "short",
                });
                let orderMeta = t("No orders", "Χωρίς παραγγελίες");
                if (pill.withQty > 0 && pill.liveTotal > 0) {
                  orderMeta = `${pill.withQty} ${t("orders", "παραγγελίες")} · ${formatProcureEuroCompact(pill.liveTotal)}`;
                } else if (pill.supplierCount > 0) {
                  orderMeta = `${pill.supplierCount} ${t("suppliers", "προμηθευτές")}`;
                }
                return (
                  <button
                    key={pill.orderDate || pill.idx}
                    type="button"
                    className={`procureiq-day-pill ${isActive ? "procureiq-day-pill-active" : ""}`}
                    onClick={() => handleDayPillClick(pill.idx)}
                  >
                    <div className="procureiq-day-pill-name">{dayName}</div>
                    <div className="procureiq-day-pill-date">{dateLabel}</div>
                    <div className="procureiq-day-pill-sub">{orderMeta}</div>
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              className="procureiq-day-arrow"
              disabled={selectedDayIdx >= dayPills.length - 1}
              onClick={() => {
                const next = Math.min(selectedDayIdx + 1, dayPills.length - 1);
                handleDayPillClick(next);
                scrollDayStrip(1);
              }}
              aria-label="Next"
              title={t("Next day", "Επόμενη μέρα")}
            >
              →
            </button>
          </div>

          <>
              {blockedCount > 0 && (
                <div className="procureiq-action-block" ref={actionRequiredRef}>
                  <div className="procureiq-action-header">
                    <span style={{ fontSize: 20 }}>⚠️</span>
                    <div style={{ flex: 1 }}>
                      <div className="procureiq-action-title">
                        {blockedCount}{" "}
                        {blockedCount === 1
                          ? t("supplier below minimum", "προμηθευτής κάτω από minimum")
                          : t("suppliers below minimum", "προμηθευτές κάτω από minimum")}
                      </div>
                      <div className="procureiq-action-sub">
                        {t(
                          "Resolve before sending. Either top up, or override if you've spoken to the supplier.",
                          "Επίλυση πριν την αποστολή. Αύξηση ποσότητας ή override αν έχετε μιλήσει με τον προμηθευτή."
                        )}
                      </div>
                    </div>
                  </div>
                  {blockedSuppliers.map((supplier) => {
                    const liveTotal = procureiqSupplierLiveTotal(supplier, orderQtys);
                    const liveMin = procureiqEffectiveMinOrderEur(supplier.min_order_eur);
                    const liveShortfall = procureiqMinOrderApplies(supplier.min_order_eur)
                      ? Math.max(0, liveMin - liveTotal)
                      : 0;
                    return (
                      <div key={supplier.supplier_id} className="procureiq-action-row">
                        <div
                          className="procureiq-supplier-avatar"
                          style={{ background: procureiqColorFromName(supplier.supplier_name) }}
                        >
                          {procureiqSupplierInitial(supplier.supplier_name)}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 13, color: "var(--text-primary)" }}>
                            {procureiqDisplaySupplierName(supplier.supplier_name)}
                          </div>
                          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>
                            {formatProcureEuro(liveTotal)} {t("of", "από")}{" "}
                            {formatProcureEuro(liveMin)} · {t("short", "έλλειμμα")}{" "}
                            {formatProcureEuro(liveShortfall)}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 6, flexShrink: 0, flexWrap: "wrap" }}>
                          <button
                            type="button"
                            className="btn-base btn-outline btn-small"
                            disabled={actionBusy}
                            onClick={() =>
                              setTopUpModal({
                                supplierId: supplier.supplier_id,
                                supplierName: procureiqDisplaySupplierName(supplier.supplier_name),
                              })
                            }
                          >
                            {t("Top up", "Αύξηση")}
                          </button>
                          <button
                            type="button"
                            className="btn-base btn-outline btn-small btn-outline-purple"
                            disabled={actionBusy}
                            onClick={() => handleOverrideMinimum(supplier.supplier_id)}
                          >
                            {t("Override", "Override")}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div
                className={`procureiq-send-block ${
                  sendBlocked || !canSendSelectedDay ? "is-blocked" : ""
                }`}
              >
                <div className="procureiq-send-header">
                  <div>
                    <div className="procureiq-send-title">
                      {canSendSelectedDay
                        ? t("Send today", "Αποστολή σήμερα")
                        : isLeafRecommendOnly
                          ? t("Recommend only", "Μόνο πρόταση")
                          : t("Preview day", "Προεπισκόπηση ημέρας")}
                    </div>
                    <div className="procureiq-send-total-row">
                      {sendTodayTotal > 0 ? (
                        <div
                          className={`procureiq-send-total ${
                            sendBlocked || !canSendSelectedDay ? "is-blocked" : ""
                          }`}
                        >
                          {formatProcureEuroCompact(sendTodayTotal)}
                        </div>
                      ) : null}
                      <div className="procureiq-send-meta">
                        {supplierCount}{" "}
                        {supplierCount === 1 ? t("supplier", "προμηθευτής") : t("suppliers", "προμηθευτές")}
                        {sendTodayTotal <= 0
                          ? ` · ${t("nothing to send", "τίποτα προς αποστολή")}`
                          : blockedCount > 0
                            ? ` · ${blockedCount} ${t("blocked", "μπλοκαρισμένοι")}`
                            : canSendSelectedDay
                              ? ` · ${t("queued", "στην ουρά")}`
                              : ` · ${t("preview", "προεπισκόπηση")}`}
                      </div>
                    </div>
                  </div>
                  <div className="procureiq-send-actions">
                    <button
                      type="button"
                      className="btn-base btn-green"
                      style={{ padding: "12px 22px", fontSize: 14, fontWeight: 800 }}
                      disabled={
                        sendBlocked || sendBusy || loading || !canSendSelectedDay
                      }
                      title={
                        isLeafRecommendOnly
                          ? t("Leaf forecast week — recommend only", "Εβδομάδα leaf forecast — μόνο πρόταση")
                          : !canSendSelectedDay
                            ? t("Send is only available for today", "Αποστολή μόνο για σήμερα")
                            : sendBlocked
                              ? t("Resolve all blocked suppliers first", "Επίλυση όλων των μπλοκ πρώτα")
                              : undefined
                      }
                      onClick={handleSendAll}
                    >
                      {sendBusy ? (
                        <LoadingPulse size={16} color="#fff" />
                      ) : (
                        <>📤 {t("Send All Now", "Αποστολή όλων")}</>
                      )}
                    </button>
                    {sendBlocked && (
                      <button
                        type="button"
                        className="btn-base btn-outline"
                        style={{ padding: "10px 14px", fontSize: 13 }}
                        onClick={() =>
                          actionRequiredRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
                        }
                      >
                        {t("Resolve blocked", "Επίλυση μπλοκ")}
                      </button>
                    )}
                  </div>
                </div>

                {sendBlocked && (
                  <div className="procureiq-blocked-warn">
                    <strong style={{ color: "#92400E" }}>
                      {t("⚠ Send blocked: ", "⚠ Αποστολή μπλοκαρισμένη: ")}
                    </strong>
                    {t(
                      `${blockedCount} suppliers below minimum. Empty baskets are skipped. Every non-empty supplier must be ready before sending.`,
                      `${blockedCount} προμηθευτές κάτω από minimum. Κενά καλάθια παραλείπονται. Κάθε μη κενός προμηθευτής πρέπει να είναι έτοιμος.`
                    )}
                  </div>
                )}

                {sendNotice && (
                  <div
                    style={{
                      marginBottom: 12,
                      padding: "10px 14px",
                      borderRadius: 12,
                      background: "#ECFDF5",
                      border: "1.5px solid #BBF7D0",
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#166534",
                    }}
                  >
                    {sendNotice}
                  </div>
                )}

                {suppliers.length === 0 && !loading && (
                  <p style={{ fontSize: 13, color: "var(--text-muted)", padding: "12px 0" }}>
                    {t("No orders suggested for today.", "Δεν υπάρχουν προτεινόμενες παραγγελίες για σήμερα.")}
                  </p>
                )}

                {[
                  {
                    key: "in-order",
                    title: t("In the order", "Στην παραγγελία"),
                    note: t("A quantity is already entered", "Έχει ήδη ποσότητα"),
                    suppliers: supplierGroups.inOrder,
                    open: true,
                  },
                  {
                    key: "suggested",
                    title: t("Suggested", "Προτεινόμενοι"),
                    note: t("The model suggests a buy. The basket is empty.", "Το μοντέλο προτείνει αγορά. Το καλάθι είναι κενό."),
                    suppliers: supplierGroups.suggested,
                    open: showSuggestedSuppliers,
                    onToggle: () => setShowSuggestedSuppliers((v) => !v),
                  },
                  {
                    key: "no-order",
                    title: t("No order", "Χωρίς παραγγελία"),
                    note: t("Nothing suggested and nothing in the basket", "Χωρίς πρόταση και χωρίς ποσότητα"),
                    suppliers: supplierGroups.noOrder,
                    open: showNoOrderSuppliers,
                    onToggle: () => setShowNoOrderSuppliers((v) => !v),
                  },
                ].map((group) => (
                  <section key={group.key} className="procureiq-supplier-group">
                    <button
                      type="button"
                      className="procureiq-supplier-group-title"
                      onClick={group.onToggle}
                      disabled={!group.onToggle}
                    >
                      <span>{group.title}</span>
                      <span className="procureiq-supplier-group-count">{group.suppliers.length}</span>
                      {group.onToggle ? (
                        <span className={`procureiq-chevron ${group.open ? "is-open" : ""}`}>›</span>
                      ) : null}
                    </button>
                    <div className="procureiq-supplier-group-note">{group.note}</div>
                    {group.open && group.suppliers.length === 0 && (
                      <p className="procureiq-supplier-group-empty">
                        {t("None", "Κανένας")}
                      </p>
                    )}
                    {group.open && group.suppliers.map((supplier) => (
                      <SupplierOrderCard
                        key={supplier.supplier_id}
                        supplier={supplier}
                        lang={lang}
                        t={t}
                        expanded={Boolean(expandedSuppliers[supplier.supplier_id])}
                        orderQtys={orderQtys}
                        isSending={sendingSupplierId === supplier.supplier_id}
                        actionBusy={actionBusy}
                        onToggle={() => toggleSupplierExpanded(supplier.supplier_id)}
                        onSetLineQty={setLineQty}
                        onSelectPackage={selectLinePackage}
                        onPackSuggestionAccept={acceptPackSuggestion}
                        onPackSuggestionDismiss={dismissPackSuggestion}
                        dismissedPackTips={dismissedPackTips}
                        onReset={() => resetSupplierQtys(supplier)}
                        onOverrideMin={() => handleOverrideMinimum(supplier.supplier_id)}
                      />
                    ))}
                  </section>
                ))}
              </div>

              {worthLookItems.length > 0 && (
                <div className="procureiq-worth-block">
                  <div className="procureiq-worth-header">
                    <span style={{ fontSize: 16 }}>👀</span>
                    <div style={{ fontWeight: 800, fontSize: 13, color: "var(--text-primary)" }}>
                      {t("Worth a Look", "Αξίζει μια ματιά")}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600 }}>
                      {worthLookItems.length}{" "}
                      {worthLookItems.length === 1 ? t("item", "είδος") : t("items", "είδη")}
                    </div>
                  </div>
                  {worthLookItems.map((item, idx) => (
                    <div key={`${item.product_name}-${idx}`} className="procureiq-worth-row">
                      <span style={{ fontSize: 14, color: "#991B1B" }}>🔴</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 12 }}>{item.product_name}</div>
                        <div style={{ fontSize: 10, color: "#991B1B", fontWeight: 600, marginTop: 1 }}>
                          {t("Needs stock count", "Χρειάζεται καταμέτρηση αποθέματος")}
                        </div>
                      </div>
                      <span style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 700, flexShrink: 0 }}>
                        {item.supplier_name}
                      </span>
                      {item.case_unit ? (
                        <span className="procureiq-worth-qty">{item.case_unit}</span>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}

              <div style={{ textAlign: "center", marginTop: 14 }}>
                <button
                  type="button"
                  className="btn-base btn-outline"
                  onClick={() =>
                    showProcureToast(lang === "gr" ? "Σύντομα διαθέσιμο" : "Coming soon")
                  }
                >
                  + {t("Order Something Else", "Παραγγελία κάτι άλλου")}
                </button>
              </div>
          </>
        </>
      )}

      {showStorePicker && (
        <div
          className="modal-overlay"
          onClick={(e) => e.target === e.currentTarget && setShowStorePicker(false)}
        >
          <div className="modal-box" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-handle" />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div className="modal-title" style={{ marginBottom: 0 }}>
                {t("Select store", "Επιλογή καταστήματος")}
              </div>
              <button
                type="button"
                onClick={() => setShowStorePicker(false)}
                style={{ border: "none", background: "none", fontSize: 18, cursor: "pointer", color: "var(--text-muted)" }}
              >
                ✕
              </button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {activeLocations.map((loc) => {
                const key = procureiqLocationKey(loc);
                return (
                  <button
                    key={key}
                    type="button"
                    className={`filter-chip ${selectedLocationKey === key ? "chip-active" : ""}`}
                    style={{ width: "100%", textAlign: "left", display: "flex", alignItems: "center", gap: 8 }}
                    onClick={() => {
                      if (key !== selectedLocationKey) {
                        const weekStartKey = weekStart || "";
                        const cachedWeek = getWeekOrdersCache(
                          loc.unified_location_id,
                          weekStartKey
                        );
                        const hasCachedTodayWeek =
                          (activeTab === "today" || activeTab === "week") && !!cachedWeek;
                        if (hasCachedTodayWeek) {
                          applyWeekOrdersPayload(cachedWeek);
                          setLoading(false);
                        } else {
                          setLoading(true);
                          setWeekOrders(null);
                          setSelectedOrderDate(null);
                          setOrderQtysByDate({});
                        }
                        setOrderHistory(null);
                        setOrderHistoryLoading(activeTab === "history");
                        setSuppliersAdmin(null);
                        if (canManageSuppliers && activeTab === "suppliers") {
                          setSuppliersAdminLoading(true);
                        }
                        setForecastMenu(null);
                        setForecastLeaves(null);
                        setForecastContrib(null);
                        setOrderBuild(null);
                        setOrderBuildMeta(null);
                        setConsumptionDay(null);
                        setForecastMenuLoading(activeTab === "forecast-menu");
                        setForecastLeavesLoading(activeTab === "forecast-leaves");
                        setForecastContribLoading(activeTab === "forecast-contrib");
                        setOrderBuildLoading(
                          activeTab === "order-build" || activeTab === "order-analysis"
                        );
                        setConsumptionLoading(activeTab === "consumption");
                        setLoadError(null);
                        setOrderHistoryError(null);
                        setSuppliersAdminError(null);
                        setForecastMenuError(null);
                        setForecastLeavesError(null);
                        setOrderBuildError(null);
                        setConsumptionError(null);
                      }
                      setSelectedLocationKey(key);
                      writeSelectedStore(loc.unified_location_id, loc.brand_id);
                      setShowStorePicker(false);
                    }}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: procureiqLocationDotColor(loc),
                        flexShrink: 0,
                      }}
                    />
                    {procureiqLocationWithBrandLabel(loc)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {topUpModal && (
        <div
          className="modal-overlay"
          onClick={(e) => e.target === e.currentTarget && !actionBusy && setTopUpModal(null)}
        >
          <div className="modal-box" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-handle" />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
              <div>
                <div className="modal-title" style={{ marginBottom: 4 }}>{topUpModal.supplierName}</div>
                <p style={{ fontSize: 13, color: "var(--text-secondary)", fontWeight: 600 }}>
                  {t("Top up to reach minimum", "Αύξηση για επίτευξη minimum")}
                </p>
              </div>
              <button
                type="button"
                onClick={() => !actionBusy && setTopUpModal(null)}
                style={{ border: "none", background: "none", fontSize: 18, cursor: "pointer", color: "var(--text-muted)" }}
              >
                ✕
              </button>
            </div>

            {topUpSupplierMeta && (
              <p style={{ fontSize: 13, fontWeight: 700, color: "#92400E", marginBottom: 14 }}>
                {t("Short", "Έλλειμμα")}{" "}
                {formatProcureEuro(topUpData?.gap_eur ?? topUpSupplierMeta.shortfall_eur)}{" "}
                {t("of", "από")}{" "}
                {formatProcureEuro(topUpSupplierMeta.min_order_eur)} {t("minimum", "minimum")}
              </p>
            )}

            {topUpLoading && (
              <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
                <LoadingPulse size={24} />
              </div>
            )}

            {!topUpLoading && topUpData && (topUpData.candidate_count === 0 || !topUpData.candidates?.length) && (
              <p style={{ fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6 }}>
                {t(
                  "No top-up suggestions available — add items manually or override the minimum.",
                  "Δεν υπάρχουν προτάσεις αύξησης — προσθέστε χειροκίνητα ή κάντε override το minimum."
                )}
              </p>
            )}

            {!topUpLoading && topUpData?.candidates?.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {topUpData.candidates.slice(0, 4).map((candidate) => {
                  const fillColor = procureiqFillPctColor(candidate.fill_pct);
                  const fillWidth = Math.min(100, Math.max(0, (Number(candidate.fill_pct) || 0) * 100));
                  return (
                    <div
                      key={candidate.product_id}
                      style={{
                        border: "1.5px solid var(--border)",
                        borderRadius: 14,
                        padding: 12,
                        background: "var(--surface2)",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 800, fontSize: 13, color: "var(--text-primary)" }}>
                            {candidate.product_name}
                          </div>
                          <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 2 }}>
                            {formatProcureEuro(candidate.case_cost)} {t("per case", "ανά κιβώτιο")}
                          </div>
                          <div className="procureiq-fill-track" style={{ maxWidth: 160 }}>
                            <div
                              className="procureiq-fill-bar"
                              style={{ width: `${fillWidth}%`, background: fillColor }}
                            />
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn-base btn-green btn-small"
                          disabled={actionBusy}
                          onClick={() => handleTopUpAdd(candidate)}
                        >
                          {t("Add", "Προσθήκη")}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

